import React, { useState } from 'react';
import './CaseDataForm.css';
import { Button } from './ui/button';
import { submitToSoftPro } from '../services/softproApi';
import { searchPropertyByAPN, isConfigured, setCredentials, getCredentials } from '../services/datatraceApi';
import { createTransactionRecord, saveTransaction } from '../services/datatraceTransactions';
import { useTaxSearch } from '../hooks/useTaxSearch';
import { useTitleSearch } from '../hooks/useTitleSearch';
import { ensureCountyCodePrefix, extractCountyCodeFromAPN } from '../utils/countyCodeUtils';

// Import extracted constants
import {
  AI_PROVIDERS,
  GOVERNMENT_CONTACTS,
  ORDER_CONTACTS,
  SECONDARY_CONTACTS,
  REFI_FIELDS,
  REFI_SECTION_TITLES,
  FORM_FIELDS
} from './CaseDataForm/constants';

// Import extracted helpers
import { isValueEmpty, createEmptyContact, searchSoftProContacts, getFIPSCode } from './CaseDataForm/helpers';

// Import modal components
import ApiResponseModal from './CaseDataForm/modals/ApiResponseModal';
import CredentialModal from './CaseDataForm/modals/CredentialModal';
import ErrorModal from './CaseDataForm/modals/ErrorModal';
import ContactSearchModal from './CaseDataForm/ContactSearchModal';

function CaseDataForm({ caseData, selectedProviders, onProviderToggle, onReset, fileName, orderType, refiInstructions = '', refiSelectedModels = [], treasurerContact = null, deedsContact = null, municipalityContact = null, lenderContact = null, loanOfficerContact = null, processorContact = null, dataTraceAutoResult = null, existingOrders = null }) {
  const [editedValues, setEditedValues] = useState({});
  const [selectedValue, setSelectedValue] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Refinance Workflow stages
  const [propertyVerificationStatus, setPropertyVerificationStatus] = useState('pending'); // pending, verifying, verified, needs-correction
  const [dataTraceData, setDataTraceData] = useState(null);
  const [editedDataTraceFields, setEditedDataTraceFields] = useState({}); // Track user edits to DataTrace fields
  const [dataTraceFullResponse, setDataTraceFullResponse] = useState(null);
  const [dataTraceRequestPayload, setDataTraceRequestPayload] = useState(null); // Store full API response
  const [showDataTraceModal, setShowDataTraceModal] = useState(false);
  const [showTaxModal, setShowTaxModal] = useState(false); // Show tax API response modal
  const [verificationError, setVerificationError] = useState(null); // Store verification errors
  const [showCredentialModal, setShowCredentialModal] = useState(false); // Show credential input modal
  const [secretKeyInput, setSecretKeyInput] = useState(''); // Last 3 characters of secret key

  // Tax search hook
  const {
    taxSearchStatus,
    taxSearchData,
    taxSearchError,
    startTaxSearch,
    checkTaxStatus
  } = useTaxSearch();

  // Title search hook
  const {
    titleSearchStatus,
    titleSearchData,
    titleSearchError,
    startTitleSearch,
    checkTitleStatus
  } = useTitleSearch();

  // Collapsible section state - all expanded by default
  const [collapsedSections, setCollapsedSections] = useState({
    dataExtraction: false,
    refiFields: false, // Refinance additional fields - expanded by default
    comingSoonFields: false, // Coming soon fields - expanded by default
    propertyVerification: false,
    governmentContacts: false, // Expanded by default
    softproSubmit: false,
    taxSearch: false,  // Expanded by default
    titleSearch: false, // Expanded by default
    proformaCommitment: false, // Expanded by default
    finalReview: false  // Expanded by default
  });

  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Track expanded state for condensed field views
  const [expandedFields, setExpandedFields] = useState({});

  // Contact state management
  const [governmentContacts, setGovernmentContacts] = useState(
    GOVERNMENT_CONTACTS.reduce((acc, contact) => {
      acc[contact.id] = createEmptyContact();
      return acc;
    }, {})
  );

  const [orderContacts, setOrderContacts] = useState(
    ORDER_CONTACTS.reduce((acc, contact) => {
      acc[contact.id] = createEmptyContact();
      return acc;
    }, {})
  );

  const [secondaryContacts, setSecondaryContacts] = useState(
    SECONDARY_CONTACTS.reduce((acc, contact) => {
      acc[contact.id] = createEmptyContact();
      return acc;
    }, {})
  );

  const [showVillageContact, setShowVillageContact] = useState(false);

  // Track edits to treasurer and deeds contact fields
  const [editedTreasurerContact, setEditedTreasurerContact] = useState(null);
  const [editedDeedsContact, setEditedDeedsContact] = useState(null);
  const [editedMunicipalityContact, setEditedMunicipalityContact] = useState(null);
  const [editedLenderContact, setEditedLenderContact] = useState(null);

  // Track selected contacts when multiple options are available
  const [selectedLender, setSelectedLender] = useState(null);
  const [selectedTreasurer, setSelectedTreasurer] = useState(null);
  const [selectedDeeds, setSelectedDeeds] = useState(null);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [selectedLoanOfficer, setSelectedLoanOfficer] = useState(null);
  const [selectedProcessor, setSelectedProcessor] = useState(null);

  // Track ADDITIONAL loan officers and processors (beyond the first one)
  const [additionalLoanOfficers, setAdditionalLoanOfficers] = useState([]);
  const [additionalProcessors, setAdditionalProcessors] = useState([]);

  // Modal state for adding additional contacts
  const [contactSearchModal, setContactSearchModal] = useState({
    isOpen: false,
    type: null, // 'loanOfficer' or 'processor'
    searchTerm: '',
    searchResults: [],
    isSearching: false,
    error: null
  });

  // Track validation failures for required selections
  const [lenderSelectionRequired, setLenderSelectionRequired] = useState(false);

  // Order number scheme toggle ('real' or 'test')
  const [orderNumberScheme, setOrderNumberScheme] = useState('real');

  // AI-generated notes (editable by user)
  const [aiNotes, setAiNotes] = useState(caseData?.aiNotes || caseData?.openai?.aiNotes || caseData?.claude?.aiNotes || caseData?.gemini?.aiNotes || '');

  // Search popup state
  const [searchPopup, setSearchPopup] = useState({
    isOpen: false,
    contactType: null,
    contactId: null,
    searchField: null,
    searchValue: '',
    results: [],
    isLoading: false
  });

  const openSearchPopup = (contactType, contactId, searchField, initialValue = '') => {
    setSearchPopup({
      isOpen: true,
      contactType,
      contactId,
      searchField,
      searchValue: initialValue,
      results: [],
      isLoading: false
    });
  };

  const closeSearchPopup = () => {
    setSearchPopup({
      isOpen: false,
      contactType: null,
      contactId: null,
      searchField: null,
      searchValue: '',
      results: [],
      isLoading: false
    });
  };

  const handleSearchInputChange = (value) => {
    setSearchPopup(prev => ({ ...prev, searchValue: value }));
  };

  const performSearch = async () => {
    setSearchPopup(prev => ({ ...prev, isLoading: true }));

    try {
      const results = await searchSoftProContacts(
        searchPopup.searchField,
        searchPopup.searchValue,
        searchPopup.contactType
      );
      setSearchPopup(prev => ({ ...prev, results, isLoading: false }));
    } catch (error) {
      console.error('Search failed:', error);
      setSearchPopup(prev => ({ ...prev, results: [], isLoading: false }));
    }
  };

  const selectContactFromSearch = (selectedContact) => {
    const { contactType, contactId } = searchPopup;

    const setterMap = {
      government: setGovernmentContacts,
      order: setOrderContacts,
      secondary: setSecondaryContacts
    };

    setterMap[contactType](prev => ({
      ...prev,
      [contactId]: {
        name: selectedContact.name,
        phone: selectedContact.phone,
        email: selectedContact.email
      }
    }));

    closeSearchPopup();
  };

  // Search for additional loan officers or processors
  const searchForAdditionalPerson = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setContactSearchModal(prev => ({ ...prev, error: 'Please enter at least 2 characters' }));
      return;
    }

    setContactSearchModal(prev => ({ ...prev, isSearching: true, error: null }));

    try {
      const response = await fetch(`/api/softpro/people/search?name=${encodeURIComponent(searchTerm)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (data.found && data.people && data.people.length > 0) {
        setContactSearchModal(prev => ({
          ...prev,
          searchResults: data.people,
          isSearching: false,
          error: null
        }));
      } else {
        setContactSearchModal(prev => ({
          ...prev,
          searchResults: [],
          isSearching: false,
          error: 'No matching people found in SoftPro'
        }));
      }
    } catch (error) {
      console.error('Person search failed:', error);
      setContactSearchModal(prev => ({
        ...prev,
        searchResults: [],
        isSearching: false,
        error: 'Search failed: ' + error.message
      }));
    }
  };

  // Select a person from search results and add to additional contacts
  const selectAdditionalPerson = (person) => {
    if (contactSearchModal.type === 'loanOfficer') {
      // Check for duplicates
      const isDuplicate = additionalLoanOfficers.some(lo => lo.LookupCode === person.LookupCode);
      if (isDuplicate) {
        alert('This loan officer has already been added');
        return;
      }
      setAdditionalLoanOfficers(prev => [...prev, person]);
    } else if (contactSearchModal.type === 'processor') {
      // Check for duplicates
      const isDuplicate = additionalProcessors.some(p => p.LookupCode === person.LookupCode);
      if (isDuplicate) {
        alert('This processor has already been added');
        return;
      }
      setAdditionalProcessors(prev => [...prev, person]);
    }

    // Close modal and reset
    setContactSearchModal({
      isOpen: false,
      type: null,
      searchTerm: '',
      searchResults: [],
      isSearching: false,
      error: null
    });
  };

  const handleContactChange = (contactType, contactId, field, value) => {
    const setterMap = {
      government: setGovernmentContacts,
      order: setOrderContacts,
      secondary: setSecondaryContacts
    };

    setterMap[contactType](prev => ({
      ...prev,
      [contactId]: {
        ...prev[contactId],
        [field]: value
      }
    }));
  };

  const handleFieldEdit = (fieldId, value) => {
    // Auto-fill FIPS code when county changes
    if (fieldId === 'PropertyCounty') {
      const fips = getFIPSCode(value);

      // Also update APN with county code if APN exists
      const currentAPN = editedValues['PropertyAPN'] || getMostCommonValue('PropertyAPN') || '';
      let updatedAPN = currentAPN;

      if (currentAPN && value) {
        updatedAPN = ensureCountyCodePrefix(currentAPN, value);
      }

      setEditedValues(prev => ({
        ...prev,
        [fieldId]: value,
        'PropertyFIPS': fips,
        'PropertyAPN': updatedAPN
      }));
    } else if (fieldId === 'PropertyAPN') {
      // Auto-add county code to APN if county is known
      const county = editedValues['PropertyCounty'] || getMostCommonValue('PropertyCounty') || '';
      let updatedAPN = value;

      if (value && county) {
        updatedAPN = ensureCountyCodePrefix(value, county);
      }

      setEditedValues(prev => ({
        ...prev,
        [fieldId]: updatedAPN
      }));
    } else if (fieldId === 'PropertyMunicipality') {
      // Auto-replace TWP with Township for better readability
      const updatedValue = value.replace(/\bTWP\b/gi, 'Township');

      setEditedValues(prev => ({
        ...prev,
        [fieldId]: updatedValue
      }));
    } else {
      setEditedValues(prev => ({
        ...prev,
        [fieldId]: value
      }));
    }
  };

  // Helper function to get DataTrace value for a specific field
  const getDataTraceValue = (fieldId) => {
    if (!dataTraceAutoResult) return null;

    switch (fieldId) {
      case 'PropertyCounty':
        return dataTraceAutoResult.property?.county || null;
      case 'PropertyFIPS':
        return dataTraceAutoResult.property?.countyFips || null;
      case 'PropertyAPN':
        return dataTraceAutoResult.property?.apn || null;
      case 'PropertyMunicipality':
        return dataTraceAutoResult.property?.municipality?.replace(/\bTWP\b/gi, 'Township') || null;
      default:
        return null;
    }
  };

  // Helper function to check if there's a conflict between DataTrace and AI models
  const checkDataTraceConflict = (fieldId) => {
    const dataTraceValue = getDataTraceValue(fieldId);
    if (!dataTraceValue) return false; // No DataTrace value, no conflict

    // Get AI model values (non-empty only)
    const aiValues = selectedProviders
      .map(provider => caseData[provider][fieldId])
      .filter(v => !isValueEmpty(v));

    // If all AI values are empty, no conflict (DataTrace fills the gap)
    if (aiValues.length === 0) return false;

    // Check if any AI value differs from DataTrace
    return aiValues.some(aiValue => aiValue !== dataTraceValue);
  };

  const handleValueSelect = (fieldId, provider) => {
    let value;

    // Handle DataTrace as a special provider
    if (provider === 'datatrace') {
      value = getDataTraceValue(fieldId);
    } else {
      value = caseData[provider][fieldId];
    }

    // Auto-replace TWP with Township in PropertyMunicipality
    if (fieldId === 'PropertyMunicipality' && value) {
      value = value.replace(/\bTWP\b/gi, 'Township');
    }

    setSelectedValue(prev => ({
      ...prev,
      [fieldId]: provider
    }));
    setEditedValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const checkConsensus = (fieldId) => {
    if (selectedProviders.length === 0) return true;
    const values = selectedProviders.map(provider => caseData[provider][fieldId]);

    // DO NOT include DataTrace in consensus check - it's a data source, not an AI model
    // DataTrace is only included in getMostCommonValue() to determine the final value

    // Filter out empty values
    const nonEmptyValues = values.filter(v => !isValueEmpty(v));

    // If all values are empty, return false (no consensus on actual data)
    if (nonEmptyValues.length === 0) return false;

    const uniqueValues = [...new Set(nonEmptyValues)];
    return uniqueValues.length === 1;
  };

  const getMostCommonValue = (fieldId) => {
    if (selectedProviders.length === 0) return '';

    const values = selectedProviders.map(provider => caseData[provider][fieldId]);

    // Special handling for PropertyCounty - include DataTrace result
    if (fieldId === 'PropertyCounty' && dataTraceAutoResult?.property?.county) {
      values.push(dataTraceAutoResult.property.county);
    }

    // Special handling for PropertyAPN - include DataTrace result
    if (fieldId === 'PropertyAPN' && dataTraceAutoResult?.property?.apn) {
      values.push(dataTraceAutoResult.property.apn);
    }

    // Auto-prepend county code to APN if we have county information
    if (fieldId === 'PropertyAPN') {
      // Get county from edited values first, then fall back to provider values or DataTrace
      let county = editedValues['PropertyCounty'];
      if (!county) {
        // Get county from providers
        const countyValues = selectedProviders.map(provider => caseData[provider]['PropertyCounty']);
        if (dataTraceAutoResult?.property?.county) {
          countyValues.push(dataTraceAutoResult.property.county);
        }
        const nonEmptyCounties = countyValues.filter(v => !isValueEmpty(v));
        if (nonEmptyCounties.length > 0) {
          county = nonEmptyCounties[0];
        }
      }

      if (county) {
        // Prepend county code to all APN values
        const valuesWithCountyCode = values.map(v => {
          if (v && typeof v === 'string' && v.trim()) {
            return ensureCountyCodePrefix(v, county);
          }
          return v;
        });
        // Replace values array with county-code-prefixed values
        values.length = 0;
        values.push(...valuesWithCountyCode);
      }
    }

    // Special handling for PropertyFIPS - include DataTrace result
    if (fieldId === 'PropertyFIPS' && dataTraceAutoResult?.property?.countyFips) {
      values.push(dataTraceAutoResult.property.countyFips);
    }

    // Special handling for PropertyMunicipality - include DataTrace result and replace TWP
    if (fieldId === 'PropertyMunicipality' && dataTraceAutoResult?.property?.municipality) {
      const municipalityValue = dataTraceAutoResult.property.municipality.replace(/\bTWP\b/gi, 'Township');
      console.log('Adding DataTrace municipality to values:', municipalityValue);
      values.push(municipalityValue);
    } else if (fieldId === 'PropertyMunicipality') {
      console.log('PropertyMunicipality - No DataTrace value available. dataTraceAutoResult:', dataTraceAutoResult);
    }

    // Filter out empty/invalid values
    const nonEmptyValues = values.filter(v => !isValueEmpty(v));

    // If all values are empty, return empty string
    if (nonEmptyValues.length === 0) return '';

    const valueCounts = nonEmptyValues.reduce((acc, value) => {
      // For PropertyMunicipality, normalize TWP to Township before counting
      let normalizedValue = value;
      if (fieldId === 'PropertyMunicipality') {
        normalizedValue = value.replace(/\bTWP\b/gi, 'Township');
      }
      acc[normalizedValue] = (acc[normalizedValue] || 0) + 1;
      return acc;
    }, {});

    const sortedValues = Object.entries(valueCounts).sort((a, b) => b[1] - a[1]);
    return sortedValues.length > 0 ? sortedValues[0][0] : '';
  };

  // Auto-fill FIPS code when AI extracts county value
  React.useEffect(() => {
    const county = editedValues['PropertyCounty'] || getMostCommonValue('PropertyCounty');
    const currentFips = editedValues['PropertyFIPS'] || getMostCommonValue('PropertyFIPS');

    // Only auto-fill if county has a value but FIPS is empty
    if (county && !currentFips) {
      const fips = getFIPSCode(county);
      if (fips) {
        setEditedValues(prev => ({
          ...prev,
          'PropertyFIPS': fips
        }));
      }
    }
  }, [editedValues, selectedProviders, caseData]);

  // Auto-select municipality contact when county matches property county
  React.useEffect(() => {
    if (municipalityContact?.contacts && municipalityContact.contacts.length > 1 && !selectedMunicipality) {
      const propertyCounty = dataTraceAutoResult?.property?.county;
      if (propertyCounty) {
        const matchingContact = municipalityContact.contacts.find(
          contact => contact.County && contact.County.toLowerCase() === propertyCounty.toLowerCase()
        );
        if (matchingContact) {
          setSelectedMunicipality(matchingContact);
        }
      }
    }
  }, [municipalityContact, dataTraceAutoResult, selectedMunicipality]);

  // Auto-select DataTrace as default when there's a conflict with AI models
  React.useEffect(() => {
    if (!dataTraceAutoResult) return;

    // Check each field that DataTrace provides
    const dataTraceFields = ['PropertyCounty', 'PropertyFIPS', 'PropertyAPN', 'PropertyMunicipality'];

    dataTraceFields.forEach(fieldId => {
      // Only auto-select if user hasn't manually selected a value yet
      if (!selectedValue[fieldId] && !editedValues[fieldId]) {
        // Check if there's a conflict
        if (checkDataTraceConflict(fieldId)) {
          // Set DataTrace as the default selection and fill the value
          const dataTraceValue = getDataTraceValue(fieldId);
          if (dataTraceValue) {
            setSelectedValue(prev => ({
              ...prev,
              [fieldId]: 'datatrace'
            }));
            setEditedValues(prev => ({
              ...prev,
              [fieldId]: dataTraceValue
            }));
          }
        }
      }
    });
  }, [dataTraceAutoResult, selectedValue, editedValues, selectedProviders, caseData]);

  // Property verification handler
  const handlePropertyVerification = async () => {
    setPropertyVerificationStatus('verifying');
    setVerificationError(null); // Clear previous errors

    try {
      // Check if DataTrace credentials are configured
      if (!isConfigured()) {
        setShowCredentialModal(true);
        setPropertyVerificationStatus('pending');
        return;
      }

      // Get property address components
      const address = editedValues['PropertyAddress1'] || getMostCommonValue('PropertyAddress1');
      const city = editedValues['PropertyCity'] || getMostCommonValue('PropertyCity');
      const state = editedValues['PropertyState'] || getMostCommonValue('PropertyState');
      const zip = editedValues['PropertyZip'] || getMostCommonValue('PropertyZip');
      const county = editedValues['PropertyCounty'] || getMostCommonValue('PropertyCounty');
      const countyFips = editedValues['PropertyFIPS'] || getMostCommonValue('PropertyFIPS');

      // Validate required fields
      if (!address || !city || !state) {
        const error = {
          message: 'Missing required property address fields',
          details: 'Please ensure Property Address, City, and State are filled in before verifying.',
          fields: { address, city, state, zip, county }
        };
        setVerificationError(error);
        setPropertyVerificationStatus('needs-correction');
        return;
      }

      console.log('Verifying property with DataTrace:', { address, city, state, zip, county, countyFips });

      // Call DataTrace API
      const searchParams = {
        address,
        city,
        state,
        zip,
        county: county || 'Kent', // Use extracted county or default to Kent
        countyFips: countyFips || '',
        apn: (editedValues['PropertyAPN'] || getMostCommonValue('PropertyAPN') || '').trim() // Optional APN for property search
      };

      const dataTraceResponse = await searchPropertyByAPN(searchParams);
      console.log('DataTrace Response:', dataTraceResponse);

      // Check if we got valid data
      if (!dataTraceResponse || !dataTraceResponse.property) {
        throw new Error('Invalid response from DataTrace API');
      }

      // Extract response status and transaction ID from raw API response
      const apiStatus = dataTraceResponse._rawApiResponse?.report_status || 'UNKNOWN';
      const transactionId = dataTraceResponse._rawApiResponse?.transaction_id || 'N/A';

      // Store the raw API response in the same format as transaction history
      // This ensures the modal shows exactly the same data as the API Tester
      const fullApiResponse = {
        data: dataTraceResponse._rawApiResponse,
        metadata: {
          timestamp: new Date().toISOString(),
          apiStatus: apiStatus,
          transactionId: transactionId,
          address: `${address}, ${city}, ${state} ${zip}`,
          productCode: 'property_data',
          productType: 'property_detail'
        }
      };

      setPropertyVerificationStatus('verified');
      setDataTraceData({
        apn: dataTraceResponse.property?.apn || 'N/A',
        address: dataTraceResponse.property?.address || 'N/A',
        legalDescription: dataTraceResponse.property?.legalDescription || 'Not available',
        apiStatus: apiStatus,
        transactionId: transactionId
      });
      setDataTraceFullResponse(fullApiResponse);
      setDataTraceRequestPayload(dataTraceResponse._requestBody);

      // Save transaction to history for auditing (same as API Tester)
      if (dataTraceResponse._rawApiResponse?.transaction_id) {
        try {
          const productConfig = {
            productCode: 'property_data',
            productType: 'property_detail',
            endpoint: 'property_data_sync'
          };
          const transaction = createTransactionRecord(
            searchParams,
            productConfig,
            { data: dataTraceResponse._rawApiResponse },
            dataTraceResponse._requestBody
          );
          await saveTransaction(transaction);
          console.log('Transaction saved to history:', transaction.transactionId);
        } catch (saveError) {
          console.error('Failed to save transaction to history:', saveError);
          // Don't fail the verification if transaction save fails
        }
      }

    } catch (error) {
      console.error('Property verification failed:', error);

      // Parse error message to extract structured error data if available
      let errorData = null;
      try {
        // Try to extract JSON from error message
        const detailsMatch = error.message.match(/--- Details ---\n(.*?)\n--- /s);
        if (detailsMatch) {
          errorData = JSON.parse(detailsMatch[1]);
        }
      } catch (parseError) {
        // If parsing fails, use raw error
      }

      // Check if this is a NotAvailable error
      const isNotAvailable = errorData &&
        errorData.errors &&
        errorData.errors.some(err => typeof err === 'string' && err.includes('NotAvailable'));

      if (isNotAvailable) {
        setVerificationError({
          message: 'Property data not available in DataTrace',
          errorType: 'NotAvailable',
          details: 'DataTrace does not have data for this property in their instant_recordable_legal product database.',
          suggestions: [
            '• Verify the address is correct and complete',
            '• Try using the standalone DataTrace module with different search options',
            '• The property may be new or not yet recorded in DataTrace\'s database',
            '• Consider using an alternative data source (Titlepoint or Accelerate when available)'
          ],
          transactionId: errorData.transaction_id,
          confirmationNumber: errorData.servicing_platform_confirmation_number,
          rawError: error.message,
          timestamp: new Date().toISOString()
        });
      } else {
        // Generic error
        setVerificationError({
          message: 'Property verification failed',
          details: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString()
        });
      }

      setPropertyVerificationStatus('needs-correction');
    }
  };

  // Handle credential submission
  const handleCredentialSubmit = async (e) => {
    e.preventDefault();

    try {
      const creds = getCredentials();
      const fullSecret = creds.partialSecret + secretKeyInput;

      setCredentials(creds.clientId, fullSecret, creds.scope);
      setShowCredentialModal(false);
      setSecretKeyInput('');

      // Retry verification after credentials are set
      handlePropertyVerification();
    } catch (error) {
      setVerificationError({
        message: 'Failed to configure DataTrace credentials',
        details: error.message,
        timestamp: new Date().toISOString()
      });
    }
  };

  // Tax search handler - uses custom hook
  const handleTaxSearch = async () => {
    try {
      // Get property details from verified data
      const address = editedValues['PropertyAddress1'] || getMostCommonValue('PropertyAddress1');
      const city = editedValues['PropertyCity'] || getMostCommonValue('PropertyCity');
      const state = editedValues['PropertyState'] || getMostCommonValue('PropertyState');
      const zip = editedValues['PropertyZip'] || getMostCommonValue('PropertyZip');
      const county = editedValues['PropertyCounty'] || getMostCommonValue('PropertyCounty');

      console.log('Ordering tax search from DataTrace...');

      // Call real DataTrace TaxSource API via hook
      const searchParams = {
        address,
        city,
        state,
        zip,
        county: county || 'Kent', // Use extracted county or default to Kent
        apn: dataTraceData?.apn // Use APN from property verification if available
      };

      await startTaxSearch(searchParams);
    } catch (error) {
      console.error('Tax search failed:', error);
      // Error state is already managed by the hook
    }
  };

  // Title search handler - uses custom hook
  const handleTitleSearch = async () => {
    try {
      // Get property details from verified data
      const address = editedValues['PropertyAddress1'] || getMostCommonValue('PropertyAddress1');
      const city = editedValues['PropertyCity'] || getMostCommonValue('PropertyCity');
      const state = editedValues['PropertyState'] || getMostCommonValue('PropertyState');
      const zip = editedValues['PropertyZip'] || getMostCommonValue('PropertyZip');
      const county = editedValues['PropertyCounty'] || getMostCommonValue('PropertyCounty');

      console.log('Ordering title search from DataTrace TIQSA...');

      // Call DataTrace TIQSA API via hook
      const searchParams = {
        address,
        city,
        state,
        zip,
        county: county || 'Kent', // Use extracted county or default to Kent
        apn: dataTraceData?.apn // Use APN from property verification if available
      };

      await startTitleSearch(searchParams, 'current_owner_bronze');
    } catch (error) {
      console.error('Title search failed:', error);
      // Error state is already managed by the hook
      alert('Title search failed: ' + error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check for unresolved selections BEFORE doing anything else
    const unresolved = [];

    if (lenderContact?.lenders && lenderContact.lenders.length > 1 && !selectedLender) {
      unresolved.push('lender');
    }

    if (treasurerContact?.contacts && treasurerContact.contacts.length > 1 && !selectedTreasurer) {
      unresolved.push('treasurer');
    }

    if (deedsContact?.contacts && deedsContact.contacts.length > 1 && !selectedDeeds) {
      unresolved.push('deeds');
    }

    if (municipalityContact?.contacts && municipalityContact.contacts.length > 1 && !selectedMunicipality) {
      unresolved.push('municipality');
    }

    if (loanOfficerContact?.people && loanOfficerContact.people.length > 1 && !selectedLoanOfficer) {
      unresolved.push('loan officer');
    }

    if (processorContact?.people && processorContact.people.length > 1 && !selectedProcessor) {
      unresolved.push('processor');
    }

    if (unresolved.length > 0) {
      // Set validation flags for visual feedback
      if (unresolved.includes('lender')) {
        setLenderSelectionRequired(true);
      }

      alert(`Please select a contact from the multiple options found for: ${unresolved.join(', ')}`);

      // Expand and scroll to first unresolved selection
      const sectionMap = {
        lender: '.lender-contact-section',
        treasurer: '.government-contacts-section',
        deeds: '.government-contacts-section'
      };

      // Note: We don't need to expand lender section here since it's not collapsible
      // (it doesn't use the collapsedSections state like other workflow sections)

      const firstUnresolvedSection = document.querySelector(sectionMap[unresolved[0]]);
      if (firstUnresolvedSection) {
        firstUnresolvedSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return; // Stop submission
    }

    // Reset states
    setSubmitError(null);
    setSubmitSuccess(false);
    setIsSubmitting(true);

    try {
      const finalData = {};
      FORM_FIELDS.forEach(field => {
        // Use edited value if field was explicitly edited (even if empty string)
        // Otherwise fall back to AI consensus value
        finalData[field.id] = field.id in editedValues ? editedValues[field.id] : getMostCommonValue(field.id);
      });

      // Include refinance fields (borrower and loan data) for refinance orders
      if (orderType === 'refinance') {
        REFI_FIELDS.forEach(field => {
          // Use edited value if field was explicitly edited (even if empty string)
          // Otherwise fall back to AI consensus value
          finalData[field.id] = field.id in editedValues ? editedValues[field.id] : getMostCommonValue(field.id);
        });
      }

      // Include contact data
      finalData.contacts = {
        government: governmentContacts,
        order: orderContacts,
        secondary: secondaryContacts
      };

      // Include government contacts from auto-search (treasurer, deeds, and municipality)
      // Check for both singular (contact) and plural (contacts) fields
      if (treasurerContact?.contact || treasurerContact?.contacts ||
          deedsContact?.contact || deedsContact?.contacts ||
          municipalityContact?.contact || municipalityContact?.contacts) {
        finalData.governmentContactsAutoSearch = {
          treasurer: selectedTreasurer || (treasurerContact?.contacts?.length === 1 ? treasurerContact.contacts[0] : null) || editedTreasurerContact || treasurerContact?.contact || null,
          deeds: selectedDeeds || (deedsContact?.contacts?.length === 1 ? deedsContact.contacts[0] : null) || editedDeedsContact || deedsContact?.contact || null,
          municipality: selectedMunicipality || (municipalityContact?.contacts?.length === 1 ? municipalityContact.contacts[0] : null) || editedMunicipalityContact || municipalityContact?.contact || null
        };
      }

      // Include lender contact from auto-search
      // Handle both array of lenders (lenders) and single auto-selected lender (lender)
      if (lenderContact?.lenders || lenderContact?.lender) {
        finalData.lenderAutoSearch = {
          found: lenderContact.found,
          lender: selectedLender || lenderContact.lender || (lenderContact.lenders?.length === 1 ? lenderContact.lenders[0] : null) || editedLenderContact
        };
      }

      // Include loan officer contacts from auto-search (support multiple)
      // Collect all selected loan officers into an array
      const loanOfficers = [];
      if (loanOfficerContact?.people) {
        const selectedOfficer = selectedLoanOfficer || (loanOfficerContact.people.length === 1 ? loanOfficerContact.people[0] : null);
        if (selectedOfficer) {
          loanOfficers.push(selectedOfficer);
        }
      }
      // Add additional loan officers from UI state
      if (additionalLoanOfficers && additionalLoanOfficers.length > 0) {
        additionalLoanOfficers.forEach(officer => {
          if (officer && officer.LookupCode) {
            loanOfficers.push(officer);
          }
        });
      }

      if (loanOfficers.length > 0) {
        finalData.loanOfficersAutoSearch = {
          found: true,
          people: loanOfficers
        };
      }

      // Include processor contacts from auto-search (support multiple)
      // Collect all selected processors into an array
      const processors = [];
      if (processorContact?.people) {
        const selectedProc = selectedProcessor || (processorContact.people.length === 1 ? processorContact.people[0] : null);
        if (selectedProc) {
          processors.push(selectedProc);
        }
      }
      // Add additional processors from UI state
      if (additionalProcessors && additionalProcessors.length > 0) {
        additionalProcessors.forEach(proc => {
          if (proc && proc.LookupCode) {
            processors.push(proc);
          }
        });
      }

      if (processors.length > 0) {
        finalData.processorsAutoSearch = {
          found: true,
          people: processors
        };
      }

      // Include refinance-specific data
      if (orderType === 'refinance') {
        finalData.refiInstructions = refiInstructions;
        finalData.selectedModels = refiSelectedModels;
        // Include AI-generated notes
        finalData.aiNotes = aiNotes;
      }

      // Include internal ID from MongoDB if available
      if (caseData.internalId) {
        finalData.internalId = caseData.internalId;
      }

      // Include order number scheme
      finalData.orderNumberScheme = orderNumberScheme;

      // Include existing orders if found (for RelatedOrders field in SoftPro)
      if (existingOrders && existingOrders.length > 0) {
        finalData.existingOrders = existingOrders;
      }

      console.log('Submitting to SoftPro:', finalData);

      // Submit to SoftPro API
      const response = await submitToSoftPro(finalData);

      console.log('SoftPro Response:', response);
      setSubmitSuccess(true);

      // Show success message with order number from response
      const projectName = `${finalData.PropertyAddress1 || ''}${finalData.PropertyAddress2 ? ' ' + finalData.PropertyAddress2 : ''}`;
      const orderNumber = response.orderNumber || finalData.escrowNumber || 'Auto-generated';
      alert(`Successfully submitted to SoftPro!\n\nProject: ${projectName}\nOrder Number: ${orderNumber}`);

    } catch (error) {
      console.error('Failed to submit to SoftPro:', error);
      setSubmitError(error.message || 'Failed to submit order to SoftPro');
      alert(`Error submitting to SoftPro: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reusable contact card renderer
  const renderContactCard = (contact, contactData, contactType) => {
    // Skip village if not shown
    if (contact.id === 'village' && !showVillageContact) return null;

    const renderFieldWithSearch = (fieldName, fieldType, placeholder, inputType = 'text') => (
      <div className="contact-field">
        <label>{fieldName}</label>
        <div className="contact-field-input-wrapper">
          <input
            type={inputType}
            value={contactData[contact.id]?.[fieldType] || ''}
            onChange={(e) => handleContactChange(contactType, contact.id, fieldType, e.target.value)}
            placeholder={placeholder}
          />
          <button
            type="button"
            className="search-button"
            onClick={() => openSearchPopup(
              contactType,
              contact.id,
              fieldType,
              contactData[contact.id]?.[fieldType] || ''
            )}
            title={`Search SoftPro by ${fieldName}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
          </button>
        </div>
      </div>
    );

    return (
      <div key={contact.id} className="contact-card">
        <div className="contact-card-header">
          <span className="contact-card-title">{contact.label}</span>
          {!contact.required && <span className="optional-badge">Optional</span>}
        </div>
        <div className="contact-fields">
          {renderFieldWithSearch('Name', 'name', 'Contact name')}
          {renderFieldWithSearch('Phone', 'phone', '(555) 555-5555', 'tel')}
          {renderFieldWithSearch('Email', 'email', 'email@example.com', 'email')}
        </div>
      </div>
    );
  };

  // Search popup modal component
  const renderSearchPopup = () => {
    if (!searchPopup.isOpen) return null;

    const fieldLabels = {
      name: 'Name',
      phone: 'Phone',
      email: 'Email'
    };

    return (
      <div className="search-popup-overlay" onClick={closeSearchPopup}>
        <div className="search-popup" onClick={(e) => e.stopPropagation()}>
          <div className="search-popup-header">
            <h3>Search SoftPro Contacts</h3>
            <button className="search-popup-close" onClick={closeSearchPopup}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div className="search-popup-content">
            <div className="search-popup-field">
              <label>Search by {fieldLabels[searchPopup.searchField]}</label>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  value={searchPopup.searchValue}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && performSearch()}
                  placeholder={`Enter ${fieldLabels[searchPopup.searchField].toLowerCase()} to search...`}
                  autoFocus
                />
                <button
                  type="button"
                  className="search-submit-button"
                  onClick={performSearch}
                  disabled={searchPopup.isLoading}
                >
                  {searchPopup.isLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </div>

            <div className="search-results">
              {searchPopup.isLoading && (
                <div className="search-loading">Searching SoftPro database...</div>
              )}

              {!searchPopup.isLoading && searchPopup.results.length === 0 && searchPopup.searchValue && (
                <div className="search-no-results">No contacts found. Try a different search term.</div>
              )}

              {!searchPopup.isLoading && searchPopup.results.length > 0 && (
                <div className="search-results-list">
                  <div className="search-results-header">
                    <span>Select a contact to populate all fields:</span>
                  </div>
                  {searchPopup.results.map((result) => (
                    <div
                      key={result.id}
                      className="search-result-item"
                      onClick={() => selectContactFromSearch(result)}
                    >
                      <div className="search-result-name">{result.name}</div>
                      <div className="search-result-details">
                        <span>{result.phone}</span>
                        <span>{result.email}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!searchPopup.isLoading && searchPopup.results.length === 0 && !searchPopup.searchValue && (
                <div className="search-hint">
                  Enter a search term and click Search to find contacts in SoftPro.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Special renderer for MaritalStatus toggle (only shown if there are 2 borrowers)
  const renderMaritalStatusToggle = (field) => {
    // Check if there are 2 borrowers (check if Borrower2 has any data)
    const borrower2FirstName = editedValues['Borrower2FirstName'] || getMostCommonValue('Borrower2FirstName');
    const borrower2LastName = editedValues['Borrower2LastName'] || getMostCommonValue('Borrower2LastName');

    // Don't show if only one borrower
    if (!borrower2FirstName && !borrower2LastName) {
      return null;
    }

    const hasConsensus = checkConsensus(field.id);
    const rawValue = editedValues[field.id] !== undefined
      ? editedValues[field.id]
      : getMostCommonValue(field.id);

    const currentValue = rawValue || 'Married'; // Default to Married if not set
    const isExpanded = expandedFields[field.id];
    const isEdited = editedValues[field.id] !== undefined;
    const aiValue = getMostCommonValue(field.id) || '';
    const userModified = isEdited && currentValue !== aiValue;

    return (
      <div
        key={field.id}
        className="condensed-field-group"
        onMouseEnter={() => setExpandedFields(prev => ({ ...prev, [field.id]: true }))}
        onMouseLeave={() => setExpandedFields(prev => ({ ...prev, [field.id]: false }))}
      >
        <label className="condensed-field-label">
          {field.label}
          {userModified && <span className="edited-badge" title="Manually edited">✎</span>}
        </label>

        {/* Toggle switch for Married/Unmarried */}
        <div style={{ marginTop: '8px' }}>
          <div style={{ display: 'inline-flex', border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => handleFieldEdit(field.id, 'Married')}
              style={{
                padding: '8px 20px',
                border: 'none',
                background: currentValue === 'Married' ? '#667eea' : 'white',
                color: currentValue === 'Married' ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: currentValue === 'Married' ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              Married
            </button>
            <button
              type="button"
              onClick={() => handleFieldEdit(field.id, 'Unmarried')}
              style={{
                padding: '8px 20px',
                border: 'none',
                borderLeft: '1px solid #ddd',
                background: currentValue === 'Unmarried' ? '#667eea' : 'white',
                color: currentValue === 'Unmarried' ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: currentValue === 'Unmarried' ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              Unmarried
            </button>
          </div>
        </div>

        {/* Expandable provider comparison - shown on hover/focus */}
        {isExpanded && selectedProviders.length > 0 && (
          <div className="condensed-provider-comparison">
            {selectedProviders.map(providerId => {
              const provider = AI_PROVIDERS.find(p => p.id === providerId);
              const value = caseData[providerId][field.id];
              const isSelected = selectedValue[field.id] === providerId;

              return (
                <div
                  key={providerId}
                  className={`condensed-provider-value ${isSelected ? 'selected' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleValueSelect(field.id, providerId);
                  }}
                >
                  <span className="provider-name-small">{provider.name}:</span>
                  <span className={`value-text ${value !== currentValue && !isSelected ? 'different' : ''}`}>
                    {isValueEmpty(value) ? '(empty)' : value}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderNationalRateToggle = (field) => {
    const hasConsensus = checkConsensus(field.id);
    const rawValue = editedValues[field.id] !== undefined
      ? editedValues[field.id]
      : getMostCommonValue(field.id);

    // Convert string boolean values to proper boolean
    let currentValue = rawValue;
    if (typeof rawValue === 'string') {
      currentValue = rawValue.toLowerCase() === 'true';
    } else if (typeof rawValue === 'boolean') {
      currentValue = rawValue;
    } else {
      currentValue = false; // Default to false if not set
    }

    const isExpanded = expandedFields[field.id];
    const isEdited = editedValues[field.id] !== undefined;
    const aiValue = getMostCommonValue(field.id) || '';
    const userModified = isEdited && currentValue !== aiValue;

    return (
      <div
        key={field.id}
        className="condensed-field-group"
        onMouseEnter={() => setExpandedFields(prev => ({ ...prev, [field.id]: true }))}
        onMouseLeave={() => setExpandedFields(prev => ({ ...prev, [field.id]: false }))}
      >
        <label className="condensed-field-label">
          {field.label}
          {userModified && <span className="edited-badge" title="Manually edited">✎</span>}
        </label>

        {/* Toggle switch for CLR / National Rate */}
        <div style={{ marginTop: '8px' }}>
          <div style={{ display: 'inline-flex', border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => handleFieldEdit(field.id, false)}
              style={{
                padding: '8px 20px',
                border: 'none',
                background: currentValue === false ? '#667eea' : 'white',
                color: currentValue === false ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: currentValue === false ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              False
            </button>
            <button
              type="button"
              onClick={() => handleFieldEdit(field.id, true)}
              style={{
                padding: '8px 20px',
                border: 'none',
                borderLeft: '1px solid #ddd',
                background: currentValue === true ? '#667eea' : 'white',
                color: currentValue === true ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: currentValue === true ? '600' : '400',
                transition: 'all 0.2s'
              }}
            >
              True
            </button>
          </div>
        </div>

        {/* Expandable provider comparison - shown on hover/focus */}
        {isExpanded && selectedProviders.length > 0 && (
          <div className="condensed-provider-comparison">
            {selectedProviders.map(providerId => {
              const provider = AI_PROVIDERS.find(p => p.id === providerId);
              const value = caseData[providerId][field.id];
              const isSelected = selectedValue[field.id] === providerId;

              return (
                <div
                  key={providerId}
                  className={`condensed-provider-value ${isSelected ? 'selected' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleValueSelect(field.id, providerId);
                  }}
                >
                  <span className="provider-name-small">{provider.name}:</span>
                  <span className={`value-text ${value !== currentValue && !isSelected ? 'different' : ''}`}>
                    {isValueEmpty(value) ? '(empty)' : value === true || value === 'true' ? 'True' : 'False'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Condensed field renderer for refinance fields - more compact than standard fields
  const renderCondensedField = (field) => {
    // Special handling for MaritalStatus field
    if (field.id === 'MaritalStatus') {
      return renderMaritalStatusToggle(field);
    }

    // Special handling for NationalRate field
    if (field.id === 'NationalRate') {
      return renderNationalRateToggle(field);
    }

    // Check if this field should be read-only because it's from SoftPro
    // Note: Only Name and Email fields are from SoftPro - Phone fields remain editable since SoftPro people API doesn't provide phone numbers
    const isLoanOfficerFieldFromSoftPro = (field.id === 'LoanOfficerName' || field.id === 'LoanOfficerEmail') && loanOfficerContact?.found;
    const isProcessorFieldFromSoftPro = (field.id === 'ProcessorName' || field.id === 'ProcessorEmail') && processorContact?.found;
    const isFromSoftPro = isLoanOfficerFieldFromSoftPro || isProcessorFieldFromSoftPro;

    const hasConsensus = checkConsensus(field.id);

    // Check if this field should be pre-filled from DataTrace or SoftPro
    let rawValue = editedValues[field.id] !== undefined
      ? editedValues[field.id]
      : getMostCommonValue(field.id);

    // Override with SoftPro data for loan officer fields (Name and Email only - Phone not provided by SoftPro API)
    if (loanOfficerContact?.found && loanOfficerContact?.people && loanOfficerContact.people.length > 0) {
      const softProOfficer = selectedLoanOfficer || loanOfficerContact.people[0];
      if (field.id === 'LoanOfficerName' && softProOfficer.FullName) {
        rawValue = softProOfficer.FullName;
      } else if (field.id === 'LoanOfficerEmail' && softProOfficer.Email) {
        rawValue = softProOfficer.Email;
      }
      // Note: LoanOfficerPhone is not overridden because SoftPro people API doesn't return phone numbers
    }

    // Override with SoftPro data for processor fields (Name and Email only - Phone not provided by SoftPro API)
    if (processorContact?.found && processorContact?.people && processorContact.people.length > 0) {
      const softProProcessor = selectedProcessor || processorContact.people[0];
      if (field.id === 'ProcessorName' && softProProcessor.FullName) {
        rawValue = softProProcessor.FullName;
      } else if (field.id === 'ProcessorEmail' && softProProcessor.Email) {
        rawValue = softProProcessor.Email;
      }
      // Note: ProcessorPhone is not overridden because SoftPro people API doesn't return phone numbers
    }

    // Pre-fill from DataTrace if available
    if (!rawValue && dataTraceData) {
      if (field.id === 'ParcelID' && dataTraceData.apn) {
        rawValue = dataTraceData.apn;
      } else if (field.id === 'LegalDescription' && dataTraceData.legalDescription) {
        rawValue = dataTraceData.legalDescription;
      }
    }

    const currentValue = rawValue || '';
    const isExpanded = expandedFields[field.id];
    const isEdited = editedValues[field.id] !== undefined;
    const aiValue = getMostCommonValue(field.id) || '';
    const userModified = isEdited && currentValue !== aiValue;
    const isEmpty = !currentValue || currentValue.trim() === '';

    // Special check: are all AI models empty but DataTrace has a value?
    const isDataTraceOnly = (
      (field.id === 'PropertyCounty' && dataTraceAutoResult?.property?.county) ||
      (field.id === 'PropertyFIPS' && dataTraceAutoResult?.property?.countyFips) ||
      (field.id === 'PropertyAPN' && dataTraceAutoResult?.property?.apn) ||
      (field.id === 'PropertyMunicipality' && dataTraceAutoResult?.property?.municipality)
    ) && selectedProviders.every(provider => isValueEmpty(caseData[provider][field.id]));

    // Check for DataTrace conflict with AI models
    const hasDataTraceConflict = checkDataTraceConflict(field.id);

    // Determine input class based on consensus and edit state
    let inputClass = 'condensed-input';
    if (isEmpty && !userModified) {
      // Empty fields get yellow highlight as "not found"
      inputClass += ' no-consensus-input';
    } else if (hasDataTraceConflict && !userModified) {
      // DataTrace disagrees with AI models: orange highlight
      inputClass += ' no-consensus-input';
    } else if (isDataTraceOnly && !userModified) {
      // DataTrace-only state: blue highlight
      inputClass += ' datatrace-only-input';
    } else if (hasConsensus && !userModified) {
      inputClass += ' consensus-input';
    } else if (!hasConsensus && !userModified) {
      inputClass += ' no-consensus-input';
    } else if (userModified) {
      inputClass += ' edited-input';
    }

    return (
      <div
        key={field.id}
        className="condensed-field-group"
        onMouseEnter={() => setExpandedFields(prev => ({ ...prev, [field.id]: true }))}
        onMouseLeave={() => setExpandedFields(prev => ({ ...prev, [field.id]: false }))}
      >
        <label className="condensed-field-label">
          {field.label}
          {userModified && <span className="edited-badge" title="Manually edited">✎</span>}
          {isFromSoftPro && <span className="softpro-badge" title="From SoftPro - Read-only" style={{ marginLeft: '5px', fontSize: '0.75rem', color: '#059669' }}>🔒 SoftPro</span>}
        </label>

        <div className="condensed-field-input-row">
          <input
            type="text"
            value={currentValue}
            onChange={(e) => handleFieldEdit(field.id, e.target.value)}
            onFocus={() => setExpandedFields(prev => ({ ...prev, [field.id]: true }))}
            onBlur={() => setExpandedFields(prev => ({ ...prev, [field.id]: false }))}
            className={inputClass}
            placeholder={isEmpty ? "Not Found" : `AI extracted: ${field.label}`}
            readOnly={isFromSoftPro}
            style={isFromSoftPro ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
            title={
              isFromSoftPro
                ? "This field is from SoftPro and cannot be edited"
                : userModified
                  ? "Manually edited"
                  : isEmpty
                    ? "AI could not extract this field - please enter manually"
                    : hasDataTraceConflict
                      ? "DataTrace disagrees with AI models - hover to compare and select"
                      : isDataTraceOnly
                        ? "DataTrace provided this value (AI models were empty)"
                        : hasConsensus
                          ? "All AI models agree ✓"
                          : "Models disagree - hover to compare"
            }
          />
        </div>

        {/* Expandable provider comparison - shown on hover/focus */}
        {isExpanded && selectedProviders.length > 0 && (
          <div className="condensed-provider-comparison">
            {selectedProviders.map(providerId => {
              const provider = AI_PROVIDERS.find(p => p.id === providerId);
              const value = caseData[providerId][field.id];
              const isSelected = selectedValue[field.id] === providerId;

              return (
                <div
                  key={providerId}
                  className={`condensed-provider-value ${isSelected ? 'selected' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent blur on input
                    handleValueSelect(field.id, providerId);
                  }}
                >
                  <span className="provider-name-small">{provider.name}:</span>
                  <span className={`value-text ${value !== currentValue && !isSelected ? 'different' : ''}`}>
                    {isValueEmpty(value) ? '(empty)' : value}
                  </span>
                </div>
              );
            })}
            {/* Show DataTrace for PropertyCounty field */}
            {field.id === 'PropertyCounty' && dataTraceAutoResult?.property?.county && (
              <div
                key="datatrace"
                className={`condensed-provider-value ${selectedValue[field.id] === 'datatrace' ? 'selected' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleValueSelect(field.id, 'datatrace');
                }}
              >
                <span className="provider-name-small">DataTrace:</span>
                <span className={`value-text ${dataTraceAutoResult.property.county !== currentValue && selectedValue[field.id] !== 'datatrace' ? 'different' : ''}`}>
                  {dataTraceAutoResult.property.county}
                </span>
              </div>
            )}
            {/* Show DataTrace for PropertyFIPS field */}
            {field.id === 'PropertyFIPS' && dataTraceAutoResult?.property?.countyFips && (
              <div
                key="datatrace"
                className={`condensed-provider-value ${selectedValue[field.id] === 'datatrace' ? 'selected' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleValueSelect(field.id, 'datatrace');
                }}
              >
                <span className="provider-name-small">DataTrace:</span>
                <span className={`value-text ${dataTraceAutoResult.property.countyFips !== currentValue && selectedValue[field.id] !== 'datatrace' ? 'different' : ''}`}>
                  {dataTraceAutoResult.property.countyFips}
                </span>
              </div>
            )}
            {/* Show DataTrace for PropertyAPN field */}
            {field.id === 'PropertyAPN' && dataTraceAutoResult?.property?.apn && (
              <div
                key="datatrace"
                className={`condensed-provider-value ${selectedValue[field.id] === 'datatrace' ? 'selected' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleValueSelect(field.id, 'datatrace');
                }}
              >
                <span className="provider-name-small">DataTrace:</span>
                <span className={`value-text ${dataTraceAutoResult.property.apn !== currentValue && selectedValue[field.id] !== 'datatrace' ? 'different' : ''}`}>
                  {dataTraceAutoResult.property.apn}
                </span>
              </div>
            )}
            {/* Show DataTrace for PropertyMunicipality field */}
            {field.id === 'PropertyMunicipality' && dataTraceAutoResult?.property?.municipality && (
              <div
                key="datatrace"
                className={`condensed-provider-value ${selectedValue[field.id] === 'datatrace' ? 'selected' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleValueSelect(field.id, 'datatrace');
                }}
              >
                <span className="provider-name-small">DataTrace:</span>
                <span className={`value-text ${dataTraceAutoResult.property.municipality.replace(/\bTWP\b/gi, 'Township') !== currentValue && selectedValue[field.id] !== 'datatrace' ? 'different' : ''}`}>
                  {dataTraceAutoResult.property.municipality.replace(/\bTWP\b/gi, 'Township')}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Helper function to render collapsible workflow section header
  const renderWorkflowSectionHeader = (sectionKey, title, statusBadge = null) => {
    const isCollapsed = collapsedSections[sectionKey];
    return (
      <div
        className="workflow-section-header-clickable"
        onClick={() => toggleSection(sectionKey)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <h3 className="section-title" style={{ marginBottom: 0 }}>
          <span className="collapse-indicator">{isCollapsed ? '▶' : '▼'}</span>
          {title}
          {statusBadge}
        </h3>
      </div>
    );
  };

  // Calculate workflow completion
  const getWorkflowProgress = () => {
    const steps = [
      { name: 'Data Extraction', complete: true, sectionKey: null }, // Always complete if viewing this form
      { name: 'Property Verification', complete: propertyVerificationStatus === 'verified', sectionKey: 'propertyVerification' },
      { name: 'SoftPro (Optional)', complete: submitSuccess, sectionKey: 'softproSubmit' },
      { name: 'Tax Search', complete: taxSearchStatus === 'completed', sectionKey: 'taxSearch' },
      { name: 'Title Search', complete: titleSearchStatus === 'completed', sectionKey: 'titleSearch' }
    ];
    const completedCount = steps.filter(s => s.complete).length;
    return { steps, completedCount, total: steps.length };
  };

  // Navigate to a workflow section
  const navigateToSection = (sectionKey) => {
    if (!sectionKey) return;
    // Expand the section if collapsed
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: false
    }));
    // Scroll to the section with a slight delay to ensure DOM is updated
    setTimeout(() => {
      const section = document.querySelector(`.${sectionKey}-section`);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div className="case-data-form">
      <div className="form-header">
        <button className="back-button-top" onClick={onReset}>← Back</button>
        <div className="form-header-main">
          <div>
            <h2>Review Extracted Case Data from <span className="filename-grey">{fileName}</span></h2>
          </div>
        </div>
        <div className="provider-selector">
          <label>AI Models:</label>
          <div className="provider-toggles">
            {AI_PROVIDERS.map(provider => (
              <button
                key={provider.id}
                className={`provider-toggle ${selectedProviders.includes(provider.id) ? 'active' : ''}`}
                onClick={() => onProviderToggle(provider.id)}
              >
                {provider.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="form-content">
        {/* Refinance Workflow Sections - Only show for refinance orders */}
        {orderType === 'refinance' && (
          <>
            {/* Workflow Progress Overview */}
            <div className="workflow-progress-overview">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: 0, fontSize: '1.125rem' }}>Residential Refinance Workflow</h3>
                <p className="workflow-progress-text" style={{ margin: 0 }}>
                  {getWorkflowProgress().completedCount} of {getWorkflowProgress().total} • Non-sequential
                </p>
              </div>
              <div className="workflow-progress-grid">
                {getWorkflowProgress().steps.map((step, idx) => (
                  <div
                    key={idx}
                    className={`workflow-progress-step ${step.complete ? 'completed' : 'pending'} ${step.sectionKey ? 'clickable' : ''}`}
                    onClick={() => navigateToSection(step.sectionKey)}
                    style={{ cursor: step.sectionKey ? 'pointer' : 'default' }}
                  >
                    <div className="progress-step-indicator">{step.complete ? '✓' : idx + 1}</div>
                    <div className="progress-step-label">{step.name}</div>
                  </div>
                ))}
              </div>
              <div className="workflow-progress-bar">
                <div
                  className="workflow-progress-fill"
                  style={{ width: `${(getWorkflowProgress().completedCount / getWorkflowProgress().total) * 100}%` }}
                />
              </div>
            </div>

            {/* Special Instructions Display */}
            {refiInstructions && (
              <div className="refi-workflow-section">
                <h3 className="section-title">Special Instructions</h3>
                <div className="instructions-display">
                  <pre>{refiInstructions}</pre>
                </div>
              </div>
            )}

            {/* AI Notes Section - Editable */}
            <div className="refi-workflow-section">
              <h3 className="section-title">Sunny A.I. Notes</h3>
              <p className="section-description">
                The AI has generated notes below. You can edit them if needed before submitting to SoftPro.
              </p>
              <textarea
                className="ai-notes-textarea"
                value={aiNotes}
                onChange={(e) => setAiNotes(e.target.value)}
                placeholder="AI-generated notes will appear here. You can edit this text before submitting."
                rows={6}
              />
            </div>

            {/* Property Verification Section */}
            <div className="refi-workflow-section property-verification-section propertyVerification-section">
              {renderWorkflowSectionHeader(
                'propertyVerification',
                'Property Verification',
                <>
                  {propertyVerificationStatus === 'verified' && <span className="status-badge status-success">✓ Verified</span>}
                  {propertyVerificationStatus === 'verifying' && <span className="status-badge status-pending">⏳ Verifying...</span>}
                  {propertyVerificationStatus === 'needs-correction' && <span className="status-badge status-warning">⚠ Needs Correction</span>}
                </>
              )}
              {!collapsedSections.propertyVerification && (
                <>
                  <p className="section-description">
                    Compare AI-extracted data against public records from DataTrace API
                  </p>

              <div className="verification-grid">
                <div className="verification-card">
                  <h4>AI Consensus Data</h4>
                  <div className="property-fields-verification">
                    {FORM_FIELDS.filter(f => f.section === 'property').map(field => {
                      const hasConsensus = checkConsensus(field.id);
                      const rawValue = editedValues[field.id] !== undefined
                        ? editedValues[field.id]
                        : getMostCommonValue(field.id);
                      const currentValue = rawValue || '';
                      const isExpanded = expandedFields[field.id];
                      const isEdited = editedValues[field.id] !== undefined;
                      const aiValue = getMostCommonValue(field.id) || '';
                      const userModified = isEdited && currentValue !== aiValue;
                      const isEmpty = !currentValue || currentValue.trim() === '';

                      // Special check for PropertyCounty/PropertyFIPS/PropertyMunicipality: are all AI models empty but DataTrace has a value?
                      const isDataTraceOnly = (
                        (field.id === 'PropertyCounty' && dataTraceAutoResult?.property?.county) ||
                        (field.id === 'PropertyFIPS' && dataTraceAutoResult?.property?.countyFips) ||
                        (field.id === 'PropertyMunicipality' && dataTraceAutoResult?.property?.municipality)
                      ) && selectedProviders.every(provider => isValueEmpty(caseData[provider][field.id]));

                      // Check for DataTrace conflict with AI models
                      const hasDataTraceConflict = checkDataTraceConflict(field.id);

                      // Determine input class based on consensus and edit state
                      let inputClass = 'condensed-input';
                      if (isEmpty && !userModified) {
                        inputClass += ' no-consensus-input';
                      } else if (hasDataTraceConflict && !userModified) {
                        // DataTrace disagrees with AI models: orange highlight
                        inputClass += ' no-consensus-input';
                      } else if (isDataTraceOnly && !userModified) {
                        // DataTrace-only state: blue highlight
                        inputClass += ' datatrace-only-input';
                      } else if (hasConsensus && !userModified) {
                        inputClass += ' consensus-input';
                      } else if (!hasConsensus && !userModified) {
                        inputClass += ' no-consensus-input';
                      } else if (userModified) {
                        inputClass += ' edited-input';
                      }

                      return (
                        <div
                          key={field.id}
                          className="condensed-field-group"
                          onMouseEnter={() => setExpandedFields(prev => ({ ...prev, [field.id]: true }))}
                          onMouseLeave={() => setExpandedFields(prev => ({ ...prev, [field.id]: false }))}
                        >
                          <label className="condensed-field-label">
                            {field.label}
                            {userModified && <span className="edited-badge" title="Manually edited">✎</span>}
                          </label>

                          <div className="condensed-field-input-row">
                            <input
                              type="text"
                              value={currentValue}
                              onChange={(e) => handleFieldEdit(field.id, e.target.value)}
                              onFocus={() => setExpandedFields(prev => ({ ...prev, [field.id]: true }))}
                              onBlur={() => setExpandedFields(prev => ({ ...prev, [field.id]: false }))}
                              className={inputClass}
                              placeholder={isEmpty ? "Not Found" : `AI extracted: ${field.label}`}
                              title={
                                userModified
                                  ? "Manually edited"
                                  : isEmpty
                                    ? "AI could not extract this field - please enter manually"
                                    : hasDataTraceConflict
                                      ? "DataTrace disagrees with AI models - hover to compare and select"
                                      : isDataTraceOnly
                                        ? "DataTrace provided this value (AI models were empty)"
                                        : hasConsensus
                                          ? "All AI models agree ✓"
                                          : "Models disagree - hover to compare"
                              }
                            />
                            {/* Show county code indicator for PropertyAPN field */}
                            {field.id === 'PropertyAPN' && currentValue && (() => {
                              const countyCode = extractCountyCodeFromAPN(currentValue);
                              const county = editedValues['PropertyCounty'] || getMostCommonValue('PropertyCounty') || '';
                              if (countyCode && county) {
                                return (
                                  <span
                                    className="county-code-badge"
                                    title={`County Code: ${countyCode} (${county} County)`}
                                    style={{
                                      marginLeft: '8px',
                                      padding: '2px 8px',
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem',
                                      fontWeight: '600'
                                    }}
                                  >
                                    {countyCode}
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>

                          {/* Expandable provider comparison - shown on hover/focus */}
                          {isExpanded && selectedProviders.length > 0 && (
                            <div className="condensed-provider-comparison">
                              {selectedProviders.map(providerId => {
                                const provider = AI_PROVIDERS.find(p => p.id === providerId);
                                const value = caseData[providerId][field.id];
                                const isSelected = selectedValue[field.id] === providerId;

                                return (
                                  <div
                                    key={providerId}
                                    className={`condensed-provider-value ${isSelected ? 'selected' : ''}`}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      handleValueSelect(field.id, providerId);
                                    }}
                                  >
                                    <span className="provider-icon">{provider.icon}</span>
                                    <span className="provider-name-small">{provider.name}:</span>
                                    <span className={`value-text ${value !== currentValue && !isSelected ? 'different' : ''}`}>
                                      {isValueEmpty(value) ? '(empty)' : value}
                                    </span>
                                  </div>
                                );
                              })}
                              {/* Show DataTrace for PropertyCounty field */}
                              {field.id === 'PropertyCounty' && dataTraceAutoResult?.property?.county && (
                                <div
                                  key="datatrace"
                                  className={`condensed-provider-value ${selectedValue[field.id] === 'datatrace' ? 'selected' : ''}`}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleValueSelect(field.id, 'datatrace');
                                  }}
                                >
                                  <span className="provider-name-small">DataTrace:</span>
                                  <span className={`value-text ${dataTraceAutoResult.property.county !== currentValue && selectedValue[field.id] !== 'datatrace' ? 'different' : ''}`}>
                                    {dataTraceAutoResult.property.county}
                                  </span>
                                </div>
                              )}
                              {/* Show DataTrace for PropertyFIPS field */}
                              {field.id === 'PropertyFIPS' && dataTraceAutoResult?.property?.countyFips && (
                                <div
                                  key="datatrace"
                                  className={`condensed-provider-value ${selectedValue[field.id] === 'datatrace' ? 'selected' : ''}`}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleValueSelect(field.id, 'datatrace');
                                  }}
                                >
                                  <span className="provider-name-small">DataTrace:</span>
                                  <span className={`value-text ${dataTraceAutoResult.property.countyFips !== currentValue && selectedValue[field.id] !== 'datatrace' ? 'different' : ''}`}>
                                    {dataTraceAutoResult.property.countyFips}
                                  </span>
                                </div>
                              )}
                              {/* Show DataTrace for PropertyAPN field */}
                              {field.id === 'PropertyAPN' && dataTraceAutoResult?.property?.apn && (
                                <div
                                  key="datatrace"
                                  className={`condensed-provider-value ${selectedValue[field.id] === 'datatrace' ? 'selected' : ''}`}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleValueSelect(field.id, 'datatrace');
                                  }}
                                >
                                  <span className="provider-name-small">DataTrace:</span>
                                  <span className={`value-text ${dataTraceAutoResult.property.apn !== currentValue && selectedValue[field.id] !== 'datatrace' ? 'different' : ''}`}>
                                    {dataTraceAutoResult.property.apn}
                                  </span>
                                </div>
                              )}
                              {/* Show DataTrace for PropertyMunicipality field */}
                              {field.id === 'PropertyMunicipality' && dataTraceAutoResult?.property?.municipality && (
                                <div
                                  key="datatrace"
                                  className={`condensed-provider-value ${selectedValue[field.id] === 'datatrace' ? 'selected' : ''}`}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleValueSelect(field.id, 'datatrace');
                                  }}
                                >
                                  <span className="provider-name-small">DataTrace:</span>
                                  <span className={`value-text ${dataTraceAutoResult.property.municipality.replace(/\bTWP\b/gi, 'Township') !== currentValue && selectedValue[field.id] !== 'datatrace' ? 'different' : ''}`}>
                                    {dataTraceAutoResult.property.municipality.replace(/\bTWP\b/gi, 'Township')}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="verification-card">
                  <h4>Public Records Verification</h4>
                  {dataTraceFullResponse ? (
                    <div className="history-item" style={{ marginTop: '12px' }}>
                      <div className="history-item-header">
                        <div className="history-item-info">
                          <span className="history-status-badge success">
                            Matching Property
                          </span>
                          <span className="history-timestamp">
                            {new Date(dataTraceFullResponse.metadata?.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <div className="history-item-actions">
                          <button
                            type="button"
                            className="history-view-btn"
                            onClick={() => setShowDataTraceModal(true)}
                            title="View request and response payloads"
                          >
                            📄 View Details
                          </button>
                        </div>
                      </div>
                      <div className="history-item-details">
                        <div><strong>Address:</strong> {dataTraceFullResponse.metadata?.address || 'N/A'}</div>
                        <div><strong>Owner:</strong> {dataTraceFullResponse.data?.multiple_properties?.[0]?.owner || dataTraceFullResponse.data?.buyer || 'N/A'}</div>
                        <div><strong>APN:</strong> {dataTraceFullResponse.data?.parcel_id || dataTraceFullResponse.data?.multiple_properties?.[0]?.apn || 'N/A'}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="verification-placeholder">
                      <p>Click "Check DataTrace" below to verify against public records</p>
                    </div>
                  )}

                  <button
                    type="button"
                    className="verify-button"
                    onClick={handlePropertyVerification}
                    disabled={propertyVerificationStatus === 'verifying'}
                    style={{ marginTop: '15px', width: '100%' }}
                  >
                    {propertyVerificationStatus === 'verifying' ? 'Checking...' : 'Check DataTrace'}
                  </button>
                </div>
              </div>
                </>
              )}
            </div>

            {/* Government Contacts Section - County Treasurer, Register of Deeds, and Municipality Treasurer */}
            {(treasurerContact || deedsContact || municipalityContact) && (
              <div className="refi-workflow-section government-contacts-section governmentContacts-section">
                {renderWorkflowSectionHeader(
                  'governmentContacts',
                  'Government Contacts',
                  <>
                    {((treasurerContact && treasurerContact.loading) || (deedsContact && deedsContact.loading) || (municipalityContact && municipalityContact.loading)) && <span className="status-badge status-pending">⏳ Searching...</span>}
                    {(!treasurerContact || !treasurerContact.loading) && (!deedsContact || !deedsContact.loading) && (!municipalityContact || !municipalityContact.loading) && ((treasurerContact && treasurerContact.found) || (deedsContact && deedsContact.found) || (municipalityContact && municipalityContact.found)) && <span className="status-badge status-success">✓ Contact Found</span>}
                    {(!treasurerContact || !treasurerContact.loading) && (!deedsContact || !deedsContact.loading) && (!municipalityContact || !municipalityContact.loading) && (!treasurerContact || !treasurerContact.found) && (!deedsContact || !deedsContact.found) && (!municipalityContact || !municipalityContact.found) && <span className="status-badge status-warning">⚠ Search Failed</span>}
                  </>
                )}
                {!collapsedSections.governmentContacts && (
                  <>
                    <p className="section-description">
                      Government contact information automatically retrieved from SoftPro
                    </p>

                    {/* County Treasurer Contact */}
                    {treasurerContact && (treasurerContact.loading ? (
                      <div className="workflow-notification" style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div className="loading-spinner-small"></div>
                          <div>
                            <p style={{ margin: 0, fontWeight: '600' }}>Searching for County Treasurer contact...</p>
                            <p className="notification-hint" style={{ margin: '0.25rem 0 0 0' }}>
                              Checking SoftPro database for county contact information
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : treasurerContact.found && treasurerContact.contacts && treasurerContact.contacts.length > 1 ? (
                      <div className="multi-contact-selection">
                        <p className="selection-prompt">Multiple treasurer contacts found. Please select one:</p>
                        <div className="contact-cards-grid">
                          {treasurerContact.contacts.map((contact, idx) => (
                            <div
                              key={idx}
                              className={`contact-selection-card ${selectedTreasurer?.LookupCode === contact.LookupCode ? 'selected' : ''}`}
                              onClick={() => setSelectedTreasurer(contact)}
                            >
                              <div className="contact-card-header">
                                <span>{contact.Name}</span>
                                {selectedTreasurer?.LookupCode === contact.LookupCode && <span className="selected-checkmark">✓</span>}
                              </div>
                              <div className="contact-details">
                                <div><strong>Lookup Code:</strong> {contact.LookupCode}</div>
                                {contact.Address1 && <div><strong>Address:</strong> {contact.Address1}</div>}
                                {contact.City && <div>{contact.City}, {contact.State} {contact.Zip}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : treasurerContact.found && treasurerContact.contacts && treasurerContact.contacts.length === 1 ? (
                      <div className="contact-card">
                        <div className="contact-card-header">
                          <span className="contact-card-title">County Treasurer</span>
                        </div>
                        <div className="contact-fields">
                          <div className="contact-field">
                            <label>NAME</label>
                            <input
                              type="text"
                              value={treasurerContact.contacts[0].Name ?? ''}
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                          <div className="contact-field">
                            <label>LOOKUP CODE</label>
                            <input
                              type="text"
                              value={treasurerContact.contacts[0].LookupCode ?? ''}
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : treasurerContact.found && treasurerContact.contact ? (
                      <div className="contact-card">
                        <div className="contact-card-header">
                          <span className="contact-card-title">County Treasurer</span>
                        </div>
                        <div className="contact-fields">
                          <div className="contact-field">
                            <label>NAME</label>
                            <input
                              type="text"
                              value={editedTreasurerContact?.Name ?? treasurerContact.contact.Name ?? ''}
                              onChange={(e) => setEditedTreasurerContact(prev => ({
                                ...(prev || treasurerContact.contact),
                                Name: e.target.value
                              }))}
                              placeholder="Contact name"
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                          <div className="contact-field">
                            <label>LOOKUP CODE</label>
                            <input
                              type="text"
                              value={editedTreasurerContact?.LookupCode ?? treasurerContact.contact.LookupCode ?? ''}
                              onChange={(e) => setEditedTreasurerContact(prev => ({
                                ...(prev || treasurerContact.contact),
                                LookupCode: e.target.value
                              }))}
                              placeholder="SoftPro lookup code"
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="workflow-notification" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                        <p>No County Treasurer contact found</p>
                        <p className="notification-hint">
                          {treasurerContact.error
                            ? `Error: ${treasurerContact.error}`
                            : 'The County Treasurer contact for this property could not be found in SoftPro.'}
                        </p>
                      </div>
                    ))}

                    {/* Register of Deeds Contact */}
                    {deedsContact && (deedsContact.loading ? (
                      <div className="workflow-notification" style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', marginTop: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div className="loading-spinner-small"></div>
                          <div>
                            <p style={{ margin: 0, fontWeight: '600' }}>Searching for Register of Deeds contact...</p>
                            <p className="notification-hint" style={{ margin: '0.25rem 0 0 0' }}>
                              Checking SoftPro database for register of deeds information
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : deedsContact.found && deedsContact.contacts && deedsContact.contacts.length > 1 ? (
                      <div className="multi-contact-selection" style={{ marginTop: '20px' }}>
                        <p className="selection-prompt">Multiple deeds contacts found. Please select one:</p>
                        <div className="contact-cards-grid">
                          {deedsContact.contacts.map((contact, idx) => (
                            <div
                              key={idx}
                              className={`contact-selection-card ${selectedDeeds?.LookupCode === contact.LookupCode ? 'selected' : ''}`}
                              onClick={() => setSelectedDeeds(contact)}
                            >
                              <div className="contact-card-header">
                                <span>{contact.Name}</span>
                                {selectedDeeds?.LookupCode === contact.LookupCode && <span className="selected-checkmark">✓</span>}
                              </div>
                              <div className="contact-details">
                                <div><strong>Lookup Code:</strong> {contact.LookupCode}</div>
                                {contact.Address1 && <div><strong>Address:</strong> {contact.Address1}</div>}
                                {contact.City && <div>{contact.City}, {contact.State} {contact.Zip}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : deedsContact.found && deedsContact.contacts && deedsContact.contacts.length === 1 ? (
                      <div className="contact-card" style={{ marginTop: '20px' }}>
                        <div className="contact-card-header">
                          <span className="contact-card-title">Register of Deeds</span>
                        </div>
                        <div className="contact-fields">
                          <div className="contact-field">
                            <label>NAME</label>
                            <input
                              type="text"
                              value={deedsContact.contacts[0].Name ?? ''}
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                          <div className="contact-field">
                            <label>LOOKUP CODE</label>
                            <input
                              type="text"
                              value={deedsContact.contacts[0].LookupCode ?? ''}
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : deedsContact.found && deedsContact.contact ? (
                      <div className="contact-card" style={{ marginTop: '20px' }}>
                        <div className="contact-card-header">
                          <span className="contact-card-title">Register of Deeds</span>
                        </div>
                        <div className="contact-fields">
                          <div className="contact-field">
                            <label>NAME</label>
                            <input
                              type="text"
                              value={editedDeedsContact?.Name ?? deedsContact.contact.Name ?? ''}
                              onChange={(e) => setEditedDeedsContact(prev => ({
                                ...(prev || deedsContact.contact),
                                Name: e.target.value
                              }))}
                              placeholder="Contact name"
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                          <div className="contact-field">
                            <label>LOOKUP CODE</label>
                            <input
                              type="text"
                              value={editedDeedsContact?.LookupCode ?? deedsContact.contact.LookupCode ?? ''}
                              onChange={(e) => setEditedDeedsContact(prev => ({
                                ...(prev || deedsContact.contact),
                                LookupCode: e.target.value
                              }))}
                              placeholder="SoftPro lookup code"
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="workflow-notification" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', marginTop: '20px' }}>
                        <p>No Register of Deeds contact found</p>
                        <p className="notification-hint">
                          {deedsContact.error
                            ? `Error: ${deedsContact.error}`
                            : 'The Register of Deeds contact for this property could not be found in SoftPro.'}
                        </p>
                      </div>
                    ))}

                    {/* Municipality Treasurer Contact */}
                    {municipalityContact && (municipalityContact.loading ? (
                      <div className="workflow-notification" style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9', marginTop: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div className="loading-spinner-small"></div>
                          <div>
                            <p style={{ margin: 0, fontWeight: '600' }}>Searching for Municipality Treasurer contact...</p>
                            <p className="notification-hint" style={{ margin: '0.25rem 0 0 0' }}>
                              Checking SoftPro database for municipality treasurer information
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : municipalityContact.found && municipalityContact.contacts && municipalityContact.contacts.length > 1 ? (
                      <div className="multi-contact-selection" style={{ marginTop: '20px' }}>
                        <p className="selection-prompt">Multiple municipality treasurer contacts found. Please select one:</p>
                        <div className="contact-cards-grid">
                          {municipalityContact.contacts.map((contact, idx) => (
                            <div
                              key={idx}
                              className={`contact-selection-card ${selectedMunicipality?.LookupCode === contact.LookupCode ? 'selected' : ''}`}
                              onClick={() => setSelectedMunicipality(contact)}
                            >
                              <div className="contact-card-header">
                                <span>{contact.Name}</span>
                                {selectedMunicipality?.LookupCode === contact.LookupCode && <span className="selected-checkmark">✓</span>}
                              </div>
                              <div className="contact-details">
                                {contact.County && <div style={{ fontSize: '1.1em', fontWeight: '600', marginBottom: '8px' }}>{contact.County} County</div>}
                                {contact.City && <div style={{ fontSize: '1.1em', fontWeight: '600', marginBottom: '8px' }}>{contact.City}, {contact.State} {contact.Zip}</div>}
                                {contact.Address1 && <div><strong>Address:</strong> {contact.Address1}</div>}
                                {contact.Phone && <div><strong>Phone:</strong> {contact.Phone}</div>}
                                <div style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '8px' }}><strong>Lookup Code:</strong> {contact.LookupCode}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : municipalityContact.found && municipalityContact.contacts && municipalityContact.contacts.length === 1 ? (
                      <div className="contact-card" style={{ marginTop: '20px' }}>
                        <div className="contact-card-header">
                          <span className="contact-card-title">Municipality Treasurer</span>
                        </div>
                        <div className="contact-fields">
                          <div className="contact-field">
                            <label>NAME</label>
                            <input
                              type="text"
                              value={municipalityContact.contacts[0].Name ?? ''}
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                          <div className="contact-field">
                            <label>LOOKUP CODE</label>
                            <input
                              type="text"
                              value={municipalityContact.contacts[0].LookupCode ?? ''}
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : municipalityContact.found && municipalityContact.contact ? (
                      <div className="contact-card" style={{ marginTop: '20px' }}>
                        <div className="contact-card-header">
                          <span className="contact-card-title">Municipality Treasurer</span>
                        </div>
                        <div className="contact-fields">
                          <div className="contact-field">
                            <label>NAME</label>
                            <input
                              type="text"
                              value={editedMunicipalityContact?.Name ?? municipalityContact.contact.Name ?? ''}
                              onChange={(e) => setEditedMunicipalityContact(prev => ({
                                ...(prev || municipalityContact.contact),
                                Name: e.target.value
                              }))}
                              placeholder="Contact name"
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                          <div className="contact-field">
                            <label>LOOKUP CODE</label>
                            <input
                              type="text"
                              value={editedMunicipalityContact?.LookupCode ?? municipalityContact.contact.LookupCode ?? ''}
                              onChange={(e) => setEditedMunicipalityContact(prev => ({
                                ...(prev || municipalityContact.contact),
                                LookupCode: e.target.value
                              }))}
                              placeholder="SoftPro lookup code"
                              readOnly
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="workflow-notification" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', marginTop: '20px' }}>
                        <p>No Municipality Treasurer contact found</p>
                        <p className="notification-hint">
                          {municipalityContact.error
                            ? `Error: ${municipalityContact.error}`
                            : 'The Municipality Treasurer contact for this property could not be found in SoftPro.'}
                        </p>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Lender Contact Section - Lender auto-search */}
            {lenderContact && (
              <div className="refi-workflow-section lender-contact-section">
                <h3 className="section-title">
                  Lender / Mortgagee Information
                  {lenderContact.loading && <span className="status-badge status-pending">⏳ Searching...</span>}
                  {!lenderContact.loading && lenderContact.found && <span className="status-badge status-success">✓ Lender Found</span>}
                  {!lenderContact.loading && !lenderContact.found && <span className="status-badge status-warning">⚠ Search Failed</span>}
                </h3>
                <p className="section-description">
                  Lender information automatically retrieved from SoftPro
                </p>

                {lenderContact.loading ? (
                  <div className="workflow-notification" style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div className="loading-spinner-small"></div>
                      <div>
                        <p style={{ margin: 0, fontWeight: '600' }}>Searching for lender contact...</p>
                        <p className="notification-hint" style={{ margin: '0.25rem 0 0 0' }}>
                          Checking SoftPro database for lender information
                        </p>
                      </div>
                    </div>
                  </div>
                ) : lenderContact.found && lenderContact.lenders && lenderContact.lenders.length > 1 ? (
                  <div className="multi-contact-selection">
                    {lenderSelectionRequired && (
                      <div className="workflow-notification" style={{ backgroundColor: '#fef3c7', border: '2px solid #f59e0b', marginBottom: '15px' }}>
                        <p style={{ margin: 0, fontWeight: '700', color: '#92400e', fontSize: '1rem' }}>⚠ Selection Required</p>
                        <p className="notification-hint" style={{ margin: '0.5rem 0 0 0', color: '#92400e' }}>
                          Please select one of the lender contacts below before submitting to SoftPro.
                        </p>
                      </div>
                    )}
                    <p className="selection-prompt">Multiple lender contacts found. Please select one:</p>
                    <div>
                      {lenderContact.lenders.map((lender, idx) => {
                        const isThisSelected = selectedLender && selectedLender._index === idx;
                        return (
                          <div
                            key={`lender-${idx}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLender({ ...lender, _index: idx });
                              setLenderSelectionRequired(false);
                            }}
                            style={{
                              backgroundColor: isThisSelected ? '#10b981' : 'white',
                              color: isThisSelected ? 'white' : '#333',
                              border: isThisSelected ? '2px solid #059669' : '2px solid #ddd',
                              padding: '15px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              marginBottom: '15px'
                            }}
                          >
                            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                              {lender.Name} {isThisSelected ? '✓' : ''}
                            </div>
                            <div>Lookup Code: {lender.LookupCode}</div>
                            {lender.Address1 && <div>Address: {lender.Address1}</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : lenderContact.found && lenderContact.lenders && lenderContact.lenders.length === 1 ? (
                  <div className="contact-card">
                    <div className="contact-card-header">
                      <span className="contact-card-title">Lender</span>
                    </div>
                    <div className="contact-fields">
                      <div className="contact-field">
                        <label>NAME</label>
                        <input
                          type="text"
                          value={lenderContact.lenders[0].Name ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>EMAIL</label>
                        <input
                          type="text"
                          value={lenderContact.lenders[0].Email ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>LOOKUP CODE</label>
                        <input
                          type="text"
                          value={lenderContact.lenders[0].LookupCode ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>ADDRESS</label>
                        <input
                          type="text"
                          value={lenderContact.lenders[0].Address1 ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                    </div>
                  </div>
                ) : lenderContact.found && lenderContact.lender ? (
                  <div className="contact-card">
                    <div className="contact-card-header">
                      <span className="contact-card-title">Lender</span>
                    </div>
                    <div className="contact-fields">
                      <div className="contact-field">
                        <label>NAME</label>
                        <input
                          type="text"
                          value={editedLenderContact?.Name ?? lenderContact.lender.Name ?? ''}
                          onChange={(e) => setEditedLenderContact(prev => ({
                            ...(prev || lenderContact.lender),
                            Name: e.target.value
                          }))}
                          placeholder="Lender name"
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>LOOKUP CODE</label>
                        <input
                          type="text"
                          value={editedLenderContact?.LookupCode ?? lenderContact.lender.LookupCode ?? ''}
                          onChange={(e) => setEditedLenderContact(prev => ({
                            ...(prev || lenderContact.lender),
                            LookupCode: e.target.value
                          }))}
                          placeholder="SoftPro lookup code"
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="workflow-notification" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                    <p>No lender contact found</p>
                    <p className="notification-hint">
                      {lenderContact.error
                        ? `Error: ${lenderContact.error}`
                        : 'The lender contact could not be found in SoftPro. Manual lender data will be used.'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* TODO: Extract Loan Officer and Processor sections to reusable ContactSection component */}
            {/* Loan Officer Contact Section */}
            {loanOfficerContact && (
              <div className="refi-workflow-section loan-officer-contact-section">
                <h3 className="section-title">
                  Loan Officer
                  {loanOfficerContact.loading && <span className="status-badge status-pending">⏳ Searching...</span>}
                  {!loanOfficerContact.loading && loanOfficerContact.found && <span className="status-badge status-success">✓ Found</span>}
                  {!loanOfficerContact.loading && !loanOfficerContact.found && <span className="status-badge status-warning">⚠ Search Failed</span>}
                </h3>
                <p className="section-description">
                  Loan officer information automatically retrieved from SoftPro
                </p>

                {loanOfficerContact.loading ? (
                  <div className="workflow-notification" style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div className="loading-spinner-small"></div>
                      <div>
                        <p style={{ margin: 0, fontWeight: '600' }}>Searching for loan officer...</p>
                        <p className="notification-hint" style={{ margin: '0.25rem 0 0 0' }}>
                          Checking SoftPro database for loan officer information
                        </p>
                      </div>
                    </div>
                  </div>
                ) : loanOfficerContact.found && loanOfficerContact.people && loanOfficerContact.people.length > 1 ? (
                  <div className="multi-contact-selection">
                    <p className="selection-prompt">Multiple loan officers found. Please select one:</p>
                    <div>
                      {loanOfficerContact.people.map((person, idx) => {
                        const isThisSelected = selectedLoanOfficer && selectedLoanOfficer._index === idx;
                        return (
                          <div
                            key={`loan-officer-${idx}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLoanOfficer({ ...person, _index: idx });
                            }}
                            style={{
                              backgroundColor: isThisSelected ? '#10b981' : 'white',
                              color: isThisSelected ? 'white' : '#333',
                              border: isThisSelected ? '2px solid #059669' : '2px solid #ddd',
                              padding: '15px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              marginBottom: '15px'
                            }}
                          >
                            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                              {person.FullName} {isThisSelected ? '✓' : ''}
                            </div>
                            {person.Email && <div>Email: {person.Email}</div>}
                            {person.Address1 && <div>Address: {person.Address1}</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : loanOfficerContact.found && loanOfficerContact.people && loanOfficerContact.people.length === 1 ? (
                  <div className="contact-card">
                    <div className="contact-card-header">
                      <span className="contact-card-title">Loan Officer</span>
                    </div>
                    <div className="contact-fields">
                      <div className="contact-field">
                        <label>NAME</label>
                        <input
                          type="text"
                          value={loanOfficerContact.people[0].FullName ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>EMAIL</label>
                        <input
                          type="text"
                          value={loanOfficerContact.people[0].Email ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>LOOKUP CODE</label>
                        <input
                          type="text"
                          value={loanOfficerContact.people[0].LookupCode ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>ADDRESS</label>
                        <input
                          type="text"
                          value={loanOfficerContact.people[0].Address1 ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="workflow-notification" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                    <p>No loan officer found</p>
                    <p className="notification-hint">
                      {loanOfficerContact.error
                        ? `Error: ${loanOfficerContact.error}`
                        : 'The loan officer could not be found in SoftPro.'}
                    </p>
                  </div>
                )}

                {/* Add Additional Loan Officer Button */}
                {loanOfficerContact && loanOfficerContact.found && (
                  <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
                    <p style={{ margin: '0 0 10px 0', fontSize: '0.9em', color: '#6b7280' }}>
                      Need to add more loan officers? Click below to search and add additional contacts.
                    </p>
                    <Button
                      onClick={() => {
                        setContactSearchModal({
                          isOpen: true,
                          type: 'loanOfficer',
                          searchTerm: '',
                          searchResults: [],
                          isSearching: false,
                          error: null
                        });
                      }}
                    >
                      + Add Another Loan Officer
                    </Button>
                    {additionalLoanOfficers.length > 0 && (
                      <div style={{ marginTop: '15px' }}>
                        <p style={{ fontWeight: '600', marginBottom: '10px' }}>Additional Loan Officers ({additionalLoanOfficers.length}):</p>
                        {additionalLoanOfficers.map((officer, idx) => (
                          <div key={idx} style={{ padding: '10px', backgroundColor: 'white', borderRadius: '6px', marginBottom: '8px', border: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <strong>{officer.FullName}</strong>
                                <div style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '4px' }}>
                                  {officer.Email && <div>Email: {officer.Email}</div>}
                                  {officer.LookupCode && <div>Code: {officer.LookupCode}</div>}
                                </div>
                              </div>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setAdditionalLoanOfficers(prev => prev.filter((_, i) => i !== idx));
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Processor Contact Section */}
            {processorContact && (
              <div className="refi-workflow-section processor-contact-section">
                <h3 className="section-title">
                  Processor
                  {processorContact.loading && <span className="status-badge status-pending">⏳ Searching...</span>}
                  {!processorContact.loading && processorContact.found && <span className="status-badge status-success">✓ Found</span>}
                  {!processorContact.loading && !processorContact.found && <span className="status-badge status-warning">⚠ Search Failed</span>}
                </h3>
                <p className="section-description">
                  Processor information automatically retrieved from SoftPro
                </p>

                {processorContact.loading ? (
                  <div className="workflow-notification" style={{ backgroundColor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div className="loading-spinner-small"></div>
                      <div>
                        <p style={{ margin: 0, fontWeight: '600' }}>Searching for processor...</p>
                        <p className="notification-hint" style={{ margin: '0.25rem 0 0 0' }}>
                          Checking SoftPro database for processor information
                        </p>
                      </div>
                    </div>
                  </div>
                ) : processorContact.found && processorContact.people && processorContact.people.length > 1 ? (
                  <div className="multi-contact-selection">
                    <p className="selection-prompt">Multiple processors found. Please select one:</p>
                    <div>
                      {processorContact.people.map((person, idx) => {
                        const isThisSelected = selectedProcessor && selectedProcessor._index === idx;
                        return (
                          <div
                            key={`processor-${idx}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProcessor({ ...person, _index: idx });
                            }}
                            style={{
                              backgroundColor: isThisSelected ? '#10b981' : 'white',
                              color: isThisSelected ? 'white' : '#333',
                              border: isThisSelected ? '2px solid #059669' : '2px solid #ddd',
                              padding: '15px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              marginBottom: '15px'
                            }}
                          >
                            <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                              {person.FullName} {isThisSelected ? '✓' : ''}
                            </div>
                            {person.Email && <div>Email: {person.Email}</div>}
                            {person.Address1 && <div>Address: {person.Address1}</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : processorContact.found && processorContact.people && processorContact.people.length === 1 ? (
                  <div className="contact-card">
                    <div className="contact-card-header">
                      <span className="contact-card-title">Processor</span>
                    </div>
                    <div className="contact-fields">
                      <div className="contact-field">
                        <label>NAME</label>
                        <input
                          type="text"
                          value={processorContact.people[0].FullName ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>EMAIL</label>
                        <input
                          type="text"
                          value={processorContact.people[0].Email ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>LOOKUP CODE</label>
                        <input
                          type="text"
                          value={processorContact.people[0].LookupCode ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="contact-field">
                        <label>ADDRESS</label>
                        <input
                          type="text"
                          value={processorContact.people[0].Address1 ?? ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="workflow-notification" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                    <p>No processor found</p>
                    <p className="notification-hint">
                      {processorContact.error
                        ? `Error: ${processorContact.error}`
                        : 'The processor could not be found in SoftPro.'}
                    </p>
                  </div>
                )}

                {/* Add Additional Processor Button */}
                {processorContact && processorContact.found && (
                  <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
                    <p style={{ margin: '0 0 10px 0', fontSize: '0.9em', color: '#6b7280' }}>
                      Need to add more processors? Click below to search and add additional contacts.
                    </p>
                    <Button
                      onClick={() => {
                        setContactSearchModal({
                          isOpen: true,
                          type: 'processor',
                          searchTerm: '',
                          searchResults: [],
                          isSearching: false,
                          error: null
                        });
                      }}
                    >
                      + Add Another Processor
                    </Button>
                    {additionalProcessors.length > 0 && (
                      <div style={{ marginTop: '15px' }}>
                        <p style={{ fontWeight: '600', marginBottom: '10px' }}>Additional Processors ({additionalProcessors.length}):</p>
                        {additionalProcessors.map((proc, idx) => (
                          <div key={idx} style={{ padding: '10px', backgroundColor: 'white', borderRadius: '6px', marginBottom: '8px', border: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <strong>{proc.FullName}</strong>
                                <div style={{ fontSize: '0.85em', color: '#6b7280', marginTop: '4px' }}>
                                  {proc.Email && <div>Email: {proc.Email}</div>}
                                  {proc.LookupCode && <div>Code: {proc.LookupCode}</div>}
                                </div>
                              </div>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => {
                                  setAdditionalProcessors(prev => prev.filter((_, i) => i !== idx));
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Borrower & Loan Information Section */}
            <div className="refi-workflow-section refi-fields-section">
              {renderWorkflowSectionHeader(
                'refiFields',
                'Borrower & Loan Information',
                null
              )}
              {!collapsedSections.refiFields && (
                <>
                  <p className="section-description">
                    AI-extracted borrower and loan details from refinance documents
                  </p>
                  {Object.entries(REFI_SECTION_TITLES)
                    .filter(([sectionKey]) => !['loanOfficer', 'processor', 'processor2'].includes(sectionKey))
                    .map(([sectionKey, sectionTitle]) => {
                      const sectionFields = REFI_FIELDS.filter(f => f.section === sectionKey);
                      if (sectionFields.length === 0) return null;

                      return (
                        <div key={sectionKey} className="refi-subsection">
                          <h4 className="refi-subsection-title">{sectionTitle}</h4>
                          <div className="condensed-fields-grid">
                            {sectionFields.map(renderCondensedField)}
                          </div>
                        </div>
                      );
                    })}
                </>
              )}
            </div>

            {/* SoftPro Section - Combined with Additional Fields */}
            <div className="refi-workflow-section submit-order-section softproSubmit-section">
              {renderWorkflowSectionHeader(
                'softproSubmit',
                'SoftPro',
                submitSuccess && <span className="status-badge status-success">✓ Submitted</span>
              )}
              {!collapsedSections.softproSubmit && (
                <>
                  <button
                    type="submit"
                    className="submit-order-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating Order...' : 'Create Order in SoftPro'}
                  </button>

                  {/* Order Number Scheme Toggle */}
                  <div style={{ marginTop: '15px', display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                    <span style={{ fontSize: '14px', color: '#666' }}>Order Number:</span>
                    <div style={{ display: 'inline-flex', border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden' }}>
                      <button
                        type="button"
                        onClick={() => setOrderNumberScheme('real')}
                        style={{
                          padding: '6px 16px',
                          border: 'none',
                          background: orderNumberScheme === 'real' ? '#667eea' : 'white',
                          color: orderNumberScheme === 'real' ? 'white' : '#666',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: orderNumberScheme === 'real' ? '600' : '400',
                          transition: 'all 0.2s'
                        }}
                      >
                        Real
                      </button>
                      <button
                        type="button"
                        onClick={() => setOrderNumberScheme('test')}
                        style={{
                          padding: '6px 16px',
                          border: 'none',
                          borderLeft: '1px solid #ddd',
                          background: orderNumberScheme === 'test' ? '#667eea' : 'white',
                          color: orderNumberScheme === 'test' ? 'white' : '#666',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: orderNumberScheme === 'test' ? '600' : '400',
                          transition: 'all 0.2s'
                        }}
                      >
                        Test
                      </button>
                    </div>
                    <span style={{ fontSize: '12px', color: '#999' }}>
                      ({orderNumberScheme === 'real' ? 'Sequential from DB' : 'YYYYMMDDHHMMSS'})
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Tax Search Section - Always visible for refinance */}
            <div className="refi-workflow-section tax-search-section taxSearch-section">
              {renderWorkflowSectionHeader(
                'taxSearch',
                'Tax Search',
                <>
                  {taxSearchStatus === 'completed' && <span className="status-badge status-success">✓ Complete</span>}
                  {taxSearchStatus === 'pending' && <span className="status-badge status-pending">⏳ In Progress...</span>}
                  {taxSearchStatus === 'needs-review' && <span className="status-badge status-warning">⚠ Needs Review</span>}
                  {taxSearchStatus === 'not-started' && <span className="status-badge">Not Started</span>}
                </>
              )}
              {!collapsedSections.taxSearch && (
                <>
                  <p className="section-description">
                    Tax search via DataTrace TaxSource API - Can run independently or concurrently with title search
                  </p>

                {taxSearchData && !taxSearchData.error && (
                  <div className="tax-search-results">
                    <div className="data-row">
                      <span className="data-label">Tax Year:</span>
                      <span className="data-value">{taxSearchData.taxYear}</span>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Amount:</span>
                      <span className="data-value">{taxSearchData.amount}</span>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Status:</span>
                      <span className={`data-value ${taxSearchData.paid ? 'match-success' : 'match-error'}`}>
                        {taxSearchData.paid ? '✓ Paid' : '✗ Unpaid'}
                      </span>
                    </div>

                    {/* View Full Tax API Response Button */}
                    {taxSearchData.rawResponse && (
                      <div className="verification-api-link" style={{ marginTop: '15px' }}>
                        <button
                          type="button"
                          className="view-api-response-button"
                          onClick={() => setShowTaxModal(true)}
                        >
                          📄 View Full Tax API Response
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {taxSearchData && taxSearchData.error && (
                  <div className="workflow-notification" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                    <p style={{ color: '#856404' }}>⚠ Tax search failed</p>
                    <p className="notification-hint" style={{ color: '#856404' }}>{taxSearchData.errorMessage}</p>
                  </div>
                )}

                {taxSearchStatus === 'pending' && (
                  <div className="workflow-notification">
                    <p>⏳ Waiting for tax search results... This may take minutes, hours, or days.</p>
                    <p className="notification-hint">You will receive a notification when results are ready.</p>
                  </div>
                )}

                  {taxSearchStatus === 'not-started' && (
                    <button
                      type="button"
                      className="order-search-button"
                      onClick={handleTaxSearch}
                      disabled
                      style={{ backgroundColor: '#6c757d', cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      Start Tax Search (Temporarily Disabled)
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Title Search Section - Always visible for refinance */}
            <div className="refi-workflow-section title-search-section titleSearch-section">
              {renderWorkflowSectionHeader(
                'titleSearch',
                'Title Search',
                <>
                  {titleSearchStatus === 'completed' && <span className="status-badge status-success">✓ Complete</span>}
                  {titleSearchStatus === 'pending' && <span className="status-badge status-pending">⏳ In Progress...</span>}
                  {titleSearchStatus === 'not-started' && <span className="status-badge">Not Started</span>}
                </>
              )}
              {!collapsedSections.titleSearch && (
                <>
                  <p className="section-description">
                    Title search via DataTrace TIQSA API - Can run independently or concurrently with tax search
                  </p>

                {titleSearchData && (
                  <div className="title-search-results">
                    <div className="workflow-notification" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', marginBottom: '15px' }}>
                      <p style={{ color: '#856404', fontWeight: 'bold' }}>⚠️ DUMMY DATA - Title Search Not Yet Implemented</p>
                      <p className="notification-hint" style={{ color: '#856404' }}>The data below is simulated. Real DataTrace TIQSA API integration is coming soon.</p>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Status:</span>
                      <span className="data-value status-success">✓ {titleSearchData.status}</span>
                    </div>
                    <div className="data-row">
                      <span className="data-label">Documents:</span>
                      <span className="data-value">{titleSearchData.documentsCount} documents retrieved</span>
                    </div>
                  </div>
                )}

                  {titleSearchStatus === 'not-started' && (
                    <button
                      type="button"
                      className="order-search-button"
                      onClick={handleTitleSearch}
                    >
                      Start Title Search
                    </button>
                  )}

                  {titleSearchStatus === 'pending' && (
                    <div className="workflow-notification">
                      <p>⏳ Waiting for title search results... This may take minutes, hours, or days.</p>
                      <p className="notification-hint">You will receive a notification when results are ready.</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Pro-Forma Title Commitment Section */}
            <div className="refi-workflow-section proforma-commitment-section">
              {renderWorkflowSectionHeader(
                'proformaCommitment',
                'Pro-Forma Title Commitment',
                null
              )}
              {!collapsedSections.proformaCommitment && (
                <>
                  <p className="section-description">
                    Generate and issue pro-forma title commitment documents
                  </p>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '15px' }}>
                    <button
                      type="button"
                      className="order-search-button"
                      disabled
                      style={{ backgroundColor: '#6c757d', cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      View All File Info
                    </button>

                    <button
                      type="button"
                      className="order-search-button"
                      disabled
                      style={{ backgroundColor: '#6c757d', cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      AI Clear To Go
                    </button>

                    <button
                      type="button"
                      className="order-search-button"
                      disabled
                      style={{ backgroundColor: '#6c757d', cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      Generate Report
                    </button>

                    <button
                      type="button"
                      className="order-search-button"
                      disabled
                      style={{ backgroundColor: '#6c757d', cursor: 'not-allowed', opacity: 0.6 }}
                    >
                      Issue Commitment
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* Form actions removed for refinance workflow - use section buttons instead */}
        {orderType !== 'refinance' && (
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onReset} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit to SoftPro'}
            </button>
          </div>
        )}
      </form>

      {/* Search Popup Modal */}
      {renderSearchPopup()}

      {/* DataTrace API Response Modal */}
      <ApiResponseModal
        isOpen={showDataTraceModal && !!dataTraceFullResponse}
        title="DataTrace Property Verification"
        data={dataTraceFullResponse?.data}
        requestPayload={dataTraceRequestPayload}
        transactionInfo={dataTraceFullResponse?.metadata ? {
          transactionId: dataTraceFullResponse.metadata.transactionId,
          status: dataTraceFullResponse.metadata.apiStatus,
          address: dataTraceFullResponse.metadata.address,
          product: `${dataTraceFullResponse.metadata.productCode} / ${dataTraceFullResponse.metadata.productType}`,
          confirmationNumber: dataTraceFullResponse.data?.servicing_platform_confirmation_number,
          timestamp: dataTraceFullResponse.metadata.timestamp
        } : null}
        onClose={() => setShowDataTraceModal(false)}
      />

      {/* Tax API Response Modal */}
      <ApiResponseModal
        isOpen={showTaxModal && taxSearchData && !!taxSearchData.rawResponse}
        title="DataTrace Tax API Response"
        data={taxSearchData?.rawResponse}
        onClose={() => setShowTaxModal(false)}
      />

      {/* DataTrace Credential Configuration Modal */}
      <CredentialModal
        isOpen={showCredentialModal}
        secretKeyInput={secretKeyInput}
        credentials={getCredentials()}
        onSecretKeyChange={setSecretKeyInput}
        onSubmit={handleCredentialSubmit}
        onClose={() => setShowCredentialModal(false)}
      />

      {/* Verification Error Display */}
      <ErrorModal
        error={verificationError}
        onClose={() => setVerificationError(null)}
      />

      {/* Additional Contact Search Modal */}
      <ContactSearchModal
        isOpen={contactSearchModal.isOpen}
        type={contactSearchModal.type}
        searchTerm={contactSearchModal.searchTerm}
        searchResults={contactSearchModal.searchResults}
        isSearching={contactSearchModal.isSearching}
        error={contactSearchModal.error}
        onSearchTermChange={(term) => setContactSearchModal({ ...contactSearchModal, searchTerm: term })}
        onSearch={() => searchForAdditionalPerson(contactSearchModal.searchTerm)}
        onSelect={selectAdditionalPerson}
        onClose={() => setContactSearchModal({ ...contactSearchModal, isOpen: false })}
      />
    </div>
  );
}

export default CaseDataForm;
